<template>
    <div class="app-layout-root" :class="{ 'is-landing-route': isLandingRoute }">
        <!-- Compact Service Unavailability Banner on Top of Navbar (Full width) -->
        <div
            v-if="isServiceUnavailable"
            class="service-offline-banner-wrap"
        >
            <div :class="isLandingRoute ? 'landing-layout-wrapper' : layoutContainerClass">
                <div class="service-offline-banner py-1 px-3 d-flex align-items-center justify-content-center text-center">
                    <i class="bi bi-cloud-slash-fill mr-2 banner-icon"></i>
                    <span class="font-weight-bold mr-1">Service host is unreachable.</span>
                    <span class="opacity-80">Reconnecting in {{ reconnectCountdown }}s...</span>
                    <b-spinner small v-if="isReconnecting" class="ml-2 banner-spinner"></b-spinner>
                </div>
            </div>
        </div>

        <!-- Interactive Demo Banner on Top of Navbar (Full width) -->
        <div
            v-if="isDemo"
            class="interactive-demo-banner-wrap"
            data-test="banner-interactive-demo"
        >
            <div :class="isLandingRoute ? 'landing-layout-wrapper' : layoutContainerClass">
                <div class="interactive-demo-banner py-1 px-3 d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center text-truncate mr-2 demo-banner-left">
                        <span class="badge badge-primary mr-2 demo-pulse-badge flex-shrink-0" data-test="badge-demo-pulse">
                            <i class="bi bi-play-circle-fill mr-1"></i> DEMO
                        </span>
                        <span class="font-weight-bold demo-banner-text text-truncate mr-1">Interactive Demo<span class="d-none d-sm-inline"> Mode:</span></span>
                        <span class="demo-banner-subtext text-truncate opacity-90 d-none d-md-inline">
                            Exploring VelocyTrack with simulated sprint tasks, pull requests, and capacity.
                        </span>
                    </div>
                    <div class="d-flex align-items-center flex-shrink-0">
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            class="exit-demo-btn py-0 px-2 font-weight-bold d-inline-flex align-items-center mr-1"
                            @click="exitInteractiveDemo"
                            v-b-tooltip.hover.bottom="'Exit Demo and return to Landing Page'"
                            aria-label="Exit Demo and return to Landing Page"
                            data-test="btn-exit-demo"
                        >
                            <i class="bi bi-box-arrow-left mr-1"></i> Exit Demo
                        </b-button>
                        <b-button
                            size="sm"
                            variant="link"
                            class="close-demo-icon-btn p-0 text-muted"
                            @click="exitInteractiveDemo"
                            v-b-tooltip.hover.bottom="'Close Demo'"
                            aria-label="Close Demo"
                            data-test="btn-close-demo-icon"
                        >
                            <i class="bi bi-x-lg"></i>
                        </b-button>
                    </div>
                </div>
            </div>
        </div>

        <b-overlay :show="isSigningIn || isExitingDemo" class="app-overlay-container">
            <div class="app-layout-shell">
                <!-- Full-Width Themed Header -->
                <header class="app-header-shell" v-show="isAuthenticated">
                    <div :class="layoutContainerClass">
                        <b-navbar :type="userTheme === 'dark' ? 'dark' : 'light'" class="vt-navbar p-0">
                            <!-- Left: Brand -->
                            <div class="navbar-left-section d-flex align-items-center">
                                <b-navbar-brand :to="brandNavigationTarget" data-test="brand-logo" class="mb-0 position-relative brand-title text-decoration-none d-inline-flex align-items-center" :title="brandNavTitle">
                                    <img :src="logoUrl" alt="VelocyTrack Logo" class="brand-logo-img mr-0 mr-lg-2" />
                                    <span class="brand-name d-none d-lg-inline">VelocyTrack</span>
                                </b-navbar-brand>
                            </div>

                            <!-- Center: Centered Page Navigation Links -->
                            <!-- Mobile/Tablet Centered Page Navigation Dropdown (<992px) -->
                            <b-dropdown
                                size="sm"
                                :variant="userTheme === 'dark' ? 'outline-light' : 'outline-secondary'"
                                no-caret
                                class="mobile-center-nav-dropdown d-inline-flex d-lg-none mx-auto"
                                toggle-class="mobile-nav-toggle-btn d-inline-flex align-items-center font-weight-bold px-3 py-1"
                                menu-class="mobile-nav-dropdown-menu shadow-lg"
                                data-test="dropdown-mobile-nav"
                            >
                                <template #button-content>
                                    <i :class="[mobileActiveNav.icon, 'mr-2 text-info']"></i>
                                    <span>{{ mobileActiveNav.label }}</span>
                                    <b-badge v-if="inboxSummary.unreadCount > 0 && $route.path !== '/discussions'" pill variant="primary" class="ml-1 nav-inbox-badge">
                                        {{ inboxSummary.unreadCount > 99 ? '99+' : inboxSummary.unreadCount }}
                                    </b-badge>
                                    <i class="bi bi-chevron-down ml-2 small opacity-75"></i>
                                </template>
                                <b-dropdown-item to="/timelog" :active="$route.path === '/timelog'" data-test="mobile-nav-timelog">
                                    <i class="bi bi-clock-history mr-2 text-info"></i>Timelog
                                </b-dropdown-item>
                                <b-dropdown-item to="/backlog" :active="$route.path === '/backlog'" data-test="mobile-nav-backlog">
                                    <i class="bi bi-list-check mr-2 text-primary"></i>Backlog
                                </b-dropdown-item>
                                <b-dropdown-item to="/pull-requests" :active="$route.path === '/pull-requests'" data-test="mobile-nav-pull-requests">
                                    <i class="icon-azure-pr mr-2 text-info"></i>Pull Requests
                                </b-dropdown-item>
                                <b-dropdown-item to="/branches" :active="$route.path === '/branches'" data-test="mobile-nav-branches">
                                    <i class="icon-azure-branch mr-2 text-success"></i>Branches
                                </b-dropdown-item>
                                <b-dropdown-item to="/discussions" :active="$route.path === '/discussions'" data-test="mobile-nav-discussions">
                                    <div class="d-flex align-items-center justify-content-between w-100">
                                        <span><i class="bi bi-chat-left-text mr-2 text-warning"></i>Discussions</span>
                                        <b-badge v-if="inboxSummary.unreadCount > 0" pill variant="primary" class="ml-2">
                                            {{ inboxSummary.unreadCount > 99 ? '99+' : inboxSummary.unreadCount }}
                                        </b-badge>
                                    </div>
                                </b-dropdown-item>
                                <b-dropdown-divider></b-dropdown-divider>
                                <b-dropdown-header class="small font-weight-bold text-uppercase">Team Workspace</b-dropdown-header>
                                <b-dropdown-item to="/team-insights" :active="$route.path.startsWith('/team-insights')" data-test="mobile-nav-team-insights">
                                    <i class="bi bi-person-badge mr-2 text-primary"></i>Team Insights
                                </b-dropdown-item>
                                <b-dropdown-item to="/team-dashboard" :active="$route.path.startsWith('/team-dashboard')" data-test="mobile-nav-team-dashboard">
                                    <i class="bi bi-speedometer2 mr-2 text-info"></i>Team Dashboard
                                </b-dropdown-item>
                            </b-dropdown>

                            <!-- Desktop Centered Page Navigation Links (>=992px) -->
                            <b-navbar-nav class="navbar-center-nav d-none d-lg-flex">
                                <b-nav-item to="/timelog" data-test="nav-timelog">
                                    <i class="bi bi-clock-history mr-1"></i>Timelog
                                    <span v-if="!currentProjectHasBacklog" class="nav-warning-badge" v-b-tooltip.hover.bottom="'Backlog source not connected for active project'"><i class="bi bi-exclamation-triangle-fill"></i></span>
                                </b-nav-item>
                                <b-nav-item to="/backlog" data-test="nav-backlog">
                                    <i class="bi bi-list-check mr-1"></i>Backlog
                                    <span v-if="!currentProjectHasBacklog" class="nav-warning-badge" v-b-tooltip.hover.bottom="'Backlog source not connected for active project'"><i class="bi bi-exclamation-triangle-fill"></i></span>
                                </b-nav-item>
                                <b-nav-item to="/pull-requests" data-test="nav-pull-requests">
                                    <i class="icon-azure-pr mr-1"></i>Pull Requests
                                    <span v-if="!currentProjectHasRepo" class="nav-warning-badge" v-b-tooltip.hover.bottom="'Repository source not connected for active project'"><i class="bi bi-exclamation-triangle-fill"></i></span>
                                </b-nav-item>
                                <b-nav-item to="/branches" data-test="nav-branches">
                                    <i class="icon-azure-branch mr-1"></i>Branches
                                    <span v-if="!currentProjectHasRepo" class="nav-warning-badge" v-b-tooltip.hover.bottom="'Repository source not connected for active project'"><i class="bi bi-exclamation-triangle-fill"></i></span>
                                </b-nav-item>
                                <b-nav-item to="/discussions" data-test="nav-discussions">
                                    <i class="bi bi-chat-left-text mr-1"></i>Discussions
                                    <b-badge v-if="inboxSummary.unreadCount > 0" pill variant="primary" class="ml-1 nav-inbox-badge" data-test="badge-discussions-unread">
                                        {{ inboxSummary.unreadCount > 99 ? '99+' : inboxSummary.unreadCount }}
                                    </b-badge>
                                    <span v-else-if="!currentProjectHasBacklog" class="nav-warning-badge" v-b-tooltip.hover.bottom="'Backlog source not connected for active project'"><i class="bi bi-exclamation-triangle-fill"></i></span>
                                </b-nav-item>
                                <li class="nav-item dropdown nav-team-dropdown"
                                    :class="{ show: isTeamMenuOpen, active: isTeamRouteActive }"
                                    @mouseenter="onTeamMenuEnter"
                                    @mouseleave="onTeamMenuLeave">
                                    <a href="#"
                                       class="nav-link dropdown-toggle d-inline-flex align-items-center"
                                       :class="{ active: isTeamRouteActive }"
                                       data-test="nav-team"
                                       role="button"
                                       aria-haspopup="true"
                                       :aria-expanded="isTeamMenuOpen ? 'true' : 'false'"
                                       @click.prevent.stop="toggleTeamMenu">
                                        <i class="bi bi-people-fill mr-1"></i>Team
                                        <template v-if="!isDevEnv">
                                            <i class="fa-solid fa-hourglass-start text-info ml-1 nav-coming-next-icon" v-b-tooltip.hover.bottom="'Coming Next'" aria-label="Coming Next" data-test="icon-team-coming-next"></i>
                                            <span v-if="!currentProjectHasBacklog" class="nav-warning-badge" v-b-tooltip.hover.bottom="'Backlog source not connected for active project'"><i class="bi bi-exclamation-triangle-fill"></i></span>
                                        </template>
                                        <template v-else>
                                            <b-badge
                                                v-if="(pendingTeamInvitations.length + activeTeamNudges.length) > 0"
                                                pill
                                                variant="info"
                                                class="ml-1 nav-inbox-badge"
                                                data-test="badge-team-notifications"
                                            >
                                                {{ pendingTeamInvitations.length + activeTeamNudges.length }}
                                            </b-badge>
                                            <span
                                                v-else-if="!hasConfiguredTeamSpace || !currentProjectHasBacklog"
                                                class="nav-warning-badge"
                                                v-b-tooltip.hover.bottom="!hasConfiguredTeamSpace ? 'Team Space not configured yet' : 'Backlog source not connected for active project'"
                                            ><i class="bi bi-exclamation-triangle-fill"></i></span>
                                        </template>
                                    </a>
                                    <div class="dropdown-menu shadow team-hover-menu"
                                         :class="{ show: isTeamMenuOpen }"
                                         data-test="nav-team-submenu">
                                        <a
                                            href="/team-insights"
                                            class="dropdown-item d-flex align-items-center py-2"
                                            data-test="nav-team-insights"
                                            @click.prevent="navigateTeam('/team-insights')"
                                        >
                                            <i class="bi bi-person-badge text-primary mr-2" style="font-size: 1.15rem;"></i>
                                            <div>
                                                <div class="font-weight-bold team-menu-item-title">Insights</div>
                                                <div class="small text-muted team-menu-item-desc">Member profiles, worktype mix &amp; nudges</div>
                                            </div>
                                        </a>
                                        <a
                                            href="/team-dashboard"
                                            class="dropdown-item d-flex align-items-center py-2"
                                            data-test="nav-team-dashboard"
                                            @click.prevent="navigateTeam('/team-dashboard')"
                                        >
                                            <i class="bi bi-speedometer2 text-info mr-2" style="font-size: 1.15rem;"></i>
                                            <div>
                                                <div class="font-weight-bold team-menu-item-title">Dashboard</div>
                                                <div class="small text-muted team-menu-item-desc">Capacity, deliverables &amp; absences</div>
                                            </div>
                                        </a>
                                    </div>
                                </li>
                            </b-navbar-nav>

                            <!-- Right: Layout Width, Zoom, Help, Account Menu -->
                            <b-navbar-nav class="navbar-right-controls align-items-center">
                                <!-- Layout Width Control -->
                                <b-nav-item-dropdown right no-caret data-test="dropdown-layout-width" class="mr-1 icon-only-nav-item zoom-dropdown d-none d-lg-block" menu-class="zoom-dropdown-menu shadow">
                                    <template #button-content>
                                        <span v-b-tooltip.hover.bottom="'Layout Width'" class="d-flex align-items-center zoom-nav-btn" aria-label="Layout width settings">
                                            <i class="bi bi-aspect-ratio"></i>
                                        </span>
                                    </template>
                                    <b-dropdown-header class="small font-weight-bold">Layout Width</b-dropdown-header>
                                    <b-dropdown-item-button v-for="w in layoutWidths" :key="w.value" :data-test="`width-option-${w.value}`" :active="layoutWidth === w.value" @click="setLayoutWidth(w.value)">
                                        <i :class="[w.icon, 'mr-2', layoutWidth === w.value ? '' : 'text-muted']"></i>
                                        <span>{{ w.label }}</span>
                                    </b-dropdown-item-button>
                                </b-nav-item-dropdown>

                                <!-- UI Scale / Zoom Control -->
                                <b-nav-item-dropdown right no-caret data-test="dropdown-zoom" class="mr-1 icon-only-nav-item zoom-dropdown d-none d-lg-block" menu-class="zoom-dropdown-menu shadow">
                                    <template #button-content>
                                        <span v-b-tooltip.hover.bottom="`Zoom (${currentZoom}%)`" class="d-flex align-items-center zoom-nav-btn" aria-label="Display zoom settings">
                                            <i class="bi bi-zoom-in"></i>
                                        </span>
                                    </template>
                                    <b-dropdown-header class="small font-weight-bold">Display Scale</b-dropdown-header>
                                    <b-dropdown-item-button v-for="lvl in zoomLevels" :key="lvl.value" :data-test="`zoom-option-${lvl.value}`" :active="currentZoom === lvl.value" @click="setZoom(lvl.value)">
                                        <div class="d-flex justify-content-between align-items-center w-100">
                                            <span class="d-inline-flex align-items-center">
                                                <i :class="[lvl.icon, 'mr-2', currentZoom === lvl.value ? '' : 'text-muted']"></i>
                                                <span>{{ lvl.label }}</span>
                                            </span>
                                            <span class="badge ml-2" :class="currentZoom === lvl.value ? 'badge-light text-primary' : 'badge-secondary'">{{ lvl.value }}%</span>
                                        </div>
                                    </b-dropdown-item-button>
                                    <b-dropdown-divider></b-dropdown-divider>
                                    <div class="px-3 py-1 d-flex justify-content-between align-items-center">
                                        <b-button size="sm" variant="outline-secondary" class="py-0 px-2" data-test="btn-zoom-out" aria-label="Zoom out (-5%)" v-b-tooltip.hover.bottom="'Zoom Out (-5%)'" @click.stop="stepZoom(-5)" :disabled="currentZoom <= 70">
                                            <i class="bi bi-dash"></i>
                                        </b-button>
                                        <small class="text-muted font-weight-bold">{{ currentZoom }}%</small>
                                        <b-button size="sm" variant="outline-secondary" class="py-0 px-2" data-test="btn-zoom-in" aria-label="Zoom in (+5%)" v-b-tooltip.hover.bottom="'Zoom In (+5%)'" @click.stop="stepZoom(5)" :disabled="currentZoom >= 130">
                                            <i class="bi bi-plus"></i>
                                        </b-button>
                                    </div>
                                </b-nav-item-dropdown>

                                <!-- Help & Feedback Button -->
                                <b-nav-item @click.prevent="openFeedbackModal('overview')" href="#" data-test="btn-help" class="mr-2 icon-only-nav-item d-none d-lg-block" aria-label="Help and feedback" v-b-tooltip.hover.bottom="'Help & Feedback'">
                                    <i class="bi bi-question-circle"></i>
                                </b-nav-item>

                                <!-- Quick Theme Toggle (Light / Dark) -->
                                <b-nav-item
                                    @click.prevent="toggleTheme"
                                    href="#"
                                    data-test="btn-toggle-theme"
                                    class="mr-2 icon-only-nav-item theme-nav-btn d-none d-lg-block"
                                    :aria-label="userTheme === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'"
                                    v-b-tooltip.hover.bottom="userTheme === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'"
                                >
                                    <i :class="userTheme === 'dark' ? 'bi bi-sun' : 'bi bi-moon-stars'"></i>
                                </b-nav-item>

                                <b-nav-item-dropdown right no-caret data-test="dropdown-user-menu" class="account-nav-dropdown" menu-class="shadow">
                                    <template #button-content>
                                        <div class="d-flex align-items-center" v-b-tooltip.hover.bottom="'Account Menu'" aria-label="Account user menu">
                                            <b-badge v-if="isBetaUser" class="beta-avatar-badge mr-2 d-none d-lg-inline-block" data-test="badge-beta-pill">BETA</b-badge>
                                            <img :src="accountAvatar" class="account-avatar rounded-circle border border-secondary" width="32" height="32" />
                                        </div>
                                    </template>
                                    <b-dropdown-header class="small font-weight-bold text-truncate" style="max-width: 200px;">{{ accountDisplayName }}</b-dropdown-header>
                                    <b-dropdown-item to="/profile" data-test="menu-item-profile"><i class="bi bi-person-gear mr-2 text-muted"></i>Profile & Settings</b-dropdown-item>
                                    <b-dropdown-item to="/config" data-test="menu-item-config"><i class="bi bi-gear-fill mr-2 text-muted"></i>Configuration</b-dropdown-item>
                                    <b-dropdown-item v-if="(isBetaUser || isDevEnv || isDemo) && hasAcknowledgedBetaWelcome && !isBetaWelcomeModalOpen" @click.prevent="openBetaInfo" data-test="menu-item-beta-info"><i class="bi bi-stars mr-2 text-muted"></i>System &amp; Changelog</b-dropdown-item>
                                    <b-dropdown-item class="d-lg-none" @click.prevent="toggleTheme" data-test="menu-item-theme">
                                        <i :class="userTheme === 'dark' ? 'bi bi-sun mr-2 text-muted' : 'bi bi-moon-stars mr-2 text-muted'"></i>
                                        {{ userTheme === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme' }}
                                    </b-dropdown-item>
                                    <b-dropdown-item class="d-lg-none" @click.prevent="openFeedbackModal('overview')" data-test="menu-item-help">
                                        <i class="bi bi-question-circle mr-2 text-muted"></i>Help &amp; Feedback
                                    </b-dropdown-item>
                                    <b-dropdown-item @click.prevent="openLegal('terms')" data-test="menu-item-legal"><i class="bi bi-shield-check mr-2 text-muted"></i>Legal &amp; Terms</b-dropdown-item>
                                    <b-dropdown-divider></b-dropdown-divider>
                                    <b-dropdown-item @click.prevent="handleSignOut" variant="danger" data-test="btn-sign-out"><i class="bi bi-box-arrow-right mr-2 text-danger"></i>Sign Out</b-dropdown-item>
                                </b-nav-item-dropdown>
                            </b-navbar-nav>
                        </b-navbar>
                    </div>
                </header>

                <!-- Page Content Area -->
                <main :class="isLandingRoute ? 'landing-layout-wrapper' : layoutContainerClass" class="app-content-container">
                    <!-- Global Pending Team Invitations Banner -->
                    <div
                        v-if="isDevEnv && isAuthenticated && pendingTeamInvitations.length && $route.path !== '/config'"
                        class="mt-3"
                        data-test="banner-global-team-invite"
                    >
                        <div
                            v-for="inv in pendingTeamInvitations"
                            :key="inv.invitationId || inv.token"
                            class="alert alert-info border shadow-sm d-flex flex-wrap justify-content-between align-items-center mb-2 py-2 px-3"
                        >
                            <div class="mr-3">
                                <i class="bi bi-people-fill text-info mr-2"></i>
                                <strong>Team Invitation:</strong> You've been invited to join <strong>{{ inv.workspaceName }}</strong>
                                <span v-if="inv.invitedByName || inv.invitedByEmail" class="small text-muted ml-1">
                                    by {{ inv.invitedByName || inv.invitedByEmail }}
                                </span>
                            </div>
                            <div class="d-flex align-items-center mt-2 mt-sm-0">
                                <b-button
                                    variant="success"
                                    size="sm"
                                    class="mr-2 font-weight-bold"
                                    data-test="btn-global-accept-invite"
                                    @click="acceptGlobalTeamInviteAsync(inv)"
                                >
                                    <i class="bi bi-check-lg mr-1"></i>Accept &amp; Join
                                </b-button>
                                <b-button
                                    variant="outline-secondary"
                                    size="sm"
                                    data-test="btn-global-decline-invite"
                                    @click="declineGlobalTeamInviteAsync(inv)"
                                >
                                    Decline
                                </b-button>
                            </div>
                        </div>
                    </div>

                    <!-- Global Active Manager Timesheet Nudges Banner -->
                    <div
                        v-if="isAuthenticated && activeTeamNudges.length"
                        class="mt-3"
                        data-test="banner-global-team-nudge"
                    >
                        <div
                            v-for="nudge in activeTeamNudges"
                            :key="nudge.id"
                            class="alert alert-warning border shadow-sm d-flex flex-wrap justify-content-between align-items-center mb-2 py-2 px-3"
                        >
                            <div class="mr-3">
                                <i class="bi bi-bell-fill text-warning mr-2"></i>
                                <strong>Timesheet Reminder ({{ nudge.workspaceName }} &mdash; {{ nudge.periodLabel }}):</strong>
                                <span class="ml-1">{{ nudge.message }}</span>
                                <span v-if="nudge.missingHours > 0" class="badge badge-warning ml-2">
                                    {{ nudge.missingHours }}h behind
                                </span>
                            </div>
                            <div class="d-flex align-items-center mt-2 mt-sm-0">
                                <b-button
                                    v-if="$route.path !== '/timelog'"
                                    variant="primary"
                                    size="sm"
                                    to="/timelog"
                                    class="mr-2 font-weight-bold"
                                    data-test="btn-nudge-open-timelog"
                                >
                                    <i class="bi bi-clock-history mr-1"></i>Open Timelog
                                </b-button>
                                <b-button
                                    variant="outline-secondary"
                                    size="sm"
                                    data-test="btn-dismiss-nudge"
                                    @click="dismissGlobalNudgeAsync(nudge)"
                                >
                                    Dismiss
                                </b-button>
                            </div>
                        </div>
                    </div>

                    <modal-feedback-support ref="feedbackSupportModal"></modal-feedback-support>
                    <modal-beta-welcome ref="betaWelcomeModal"></modal-beta-welcome>
                    <beta-system-info v-if="isAuthenticated && !isLandingRoute && (isBetaUser || isDevEnv || isDemo) && hasAcknowledgedBetaWelcome && !isBetaWelcomeModalOpen"></beta-system-info>
                    <dev-system-sidebar v-if="isActualDevHost"></dev-system-sidebar>
                    <toast-history-tray :is-visible="isAuthenticated && !isLandingRoute"></toast-history-tray>
                    <router-view class="page-content"></router-view>
                    <modal-legal></modal-legal>
                    <cookie-consent></cookie-consent>
                </main>
            </div>
            <template #overlay>
                <load-spinner :text="isExitingDemo ? 'Exiting demo mode...' : 'Signing in...'" size="lg" />
            </template>
        </b-overlay>
    </div>
</template>

<script>
    import { BNavbar, BNavbarBrand, BNavbarNav, BNavItem, BSpinner, BButton } from 'bootstrap-vue';
    import axios from 'axios';
    // import WorkspaceStatusBar from './components/workspace-status-bar.vue';
    import ModalFeedbackSupport from './components/modal-feedback-support.vue';
    import ModalBetaWelcome from './components/modal-beta-welcome.vue';
    import ModalLegal from './components/modal-legal.vue';
    import CookieConsent from './components/cookie-consent.vue';
    import BetaSystemInfo from './components/beta-system-info.vue';
    import DevSystemSidebar from './components/dev-system-sidebar.vue';
    import ToastHistoryTray from './components/toast-history-tray.vue';
    import eventBus from './event-bus';
    import accountMixin from './mixinAccount';
    import toastMixin, { wasToastRecentlyShown } from './mixinToast';
    import { getAppEnvironment, getLogoUrl, setEnvironmentFavicons } from './envHelper';

    const envName = (process.env.VUE_APP_ENV_NAME || '').toLowerCase();
    function isEnvPROD() {
        return getAppEnvironment() === 'production';
    }

    export default {
        mixins: [accountMixin, toastMixin],
        components: {
            // 'workspace-status-bar': WorkspaceStatusBar,
            'modal-feedback-support': ModalFeedbackSupport,
            'modal-beta-welcome': ModalBetaWelcome,
            'modal-legal': ModalLegal,
            'cookie-consent': CookieConsent,
            'beta-system-info': BetaSystemInfo,
            'dev-system-sidebar': DevSystemSidebar,
            'toast-history-tray': ToastHistoryTray,
            'b-navbar': BNavbar,
            'b-navbar-brand': BNavbarBrand,
            'b-navbar-nav': BNavbarNav,
            'b-nav-item': BNavItem,
            'b-spinner': BSpinner,
            'b-button': BButton
        },
        data() {
            return {
                navbarSelectedProjectId: null,
                isBetaWelcomeModalOpen: false,
                googleClientId: process.env.VUE_APP_GOOGLE_CLIENT_ID,
                inboxSummary: {
                    totalItems: 0,
                    unreadCount: 0,
                    unansweredMentionCount: 0
                },
                verifyUrl: '/Account/GoogleSignIn',
                envName,
                isHandlingLogout: false,
                isEnvProd: isEnvPROD(),
                layoutWidth: 'standard',
                currentZoom: 100,
                last5xxToastTime: null,
                isServiceUnavailable: false,
                reconnectCountdown: 5,
                reconnectTimer: null,
                isReconnecting: false,
                lastNetworkErrorToastTime: null,
                reconnectBackoffSeconds: 5,
                zoomLevels: [
                    { value: 70, label: 'Ultra Compact', icon: 'bi bi-phone' },
                    { value: 80, label: 'Laptop Default', icon: 'bi bi-laptop' },
                    { value: 90, label: 'Comfortable', icon: 'bi bi-tablet' },
                    { value: 100, label: 'Standard (100%)', icon: 'bi bi-display' },
                    { value: 110, label: 'Large', icon: 'bi bi-tv' }
                ],
                layoutWidths: [
                    { value: 'standard', label: 'Standard', icon: 'bi bi-fullscreen-exit' },
                    { value: 'fluid', label: 'Wide', icon: 'bi bi-fullscreen' }
                ],
                isTeamMenuOpen: false,
                teamMenuLeaveTimeout: null
            }
        },
        async beforeMount() {
            console.debug('Build time:', process.env.BUILD_TIME);
            console.debug('Using Google ClientID:', process.env.VUE_APP_GOOGLE_CLIENT_ID);
            const isTouchOrMobile = () => {
                if (typeof window === 'undefined') return false;
                // Desktop and large screens (>=992px) are never considered mobile, even on touchscreen laptops
                if (window.innerWidth >= 992) return false;
                // If tablet size (768px-991px) and equipped with mouse/hover pointer, allow standard tooltips
                if (window.innerWidth >= 768 && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
                    return false;
                }
                // Mobile phone viewports (<768px) or pure touch-only devices without hover (<992px)
                return window.innerWidth < 768 || (window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches);
            };

            this.$root.$on('bv::modal::show', () => {
                this.$root.$emit('bv::hide::popover');
                this.$root.$emit('bv::hide::tooltip');
            });

            this.$root.$on('bv::tooltip::show', (bvEvent) => {
                if (isTouchOrMobile()) {
                    bvEvent.preventDefault();
                    const target = bvEvent.target;
                    if (target) {
                        setTimeout(() => {
                            if (target.removeAttribute) target.removeAttribute('title');
                        }, 0);
                    }
                }
            });

            this.$root.$on('bv::popover::show', (bvEvent) => {
                if (isTouchOrMobile() && (bvEvent.componentId?.includes('hover') || bvEvent.target?.dataset?.hoverPop !== undefined)) {
                    bvEvent.preventDefault();
                    const target = bvEvent.target;
                    if (target) {
                        setTimeout(() => {
                            if (target.removeAttribute) target.removeAttribute('title');
                        }, 0);
                    }
                }
            });

            if (typeof document !== 'undefined') {
                document.addEventListener('touchstart', (e) => {
                    if (isTouchOrMobile()) {
                        this.$root.$emit('bv::hide::tooltip');
                        const el = e.target && e.target.closest ? e.target.closest('[title]') : null;
                        if (el && el.hasAttribute('title')) {
                            const originalTitle = el.getAttribute('title');
                            el.setAttribute('data-touch-title', originalTitle);
                            el.removeAttribute('title');
                        }
                    }
                }, { passive: true, capture: true });

                document.addEventListener('click', () => {
                    if (isTouchOrMobile()) {
                        this.$root.$emit('bv::hide::tooltip');
                    }
                }, { passive: true });
            }
            this.$http.interceptors.response.use(
                response => {
                    if (this.isServiceUnavailable) {
                        this.onServiceRecovered();
                    }
                    const remHeader = response?.headers?.['x-daily-mutations-remaining'];
                    if (remHeader !== undefined && remHeader !== null) {
                        const rem = parseInt(remHeader, 10);
                        const limHeader = response?.headers?.['x-daily-mutations-limit'];
                        const lim = limHeader !== undefined ? parseInt(limHeader, 10) : 5;
                        if (!isNaN(rem)) {
                            this.$store.commit('setDailyMutationsRemaining', { remaining: rem, limit: !isNaN(lim) ? lim : 5 });
                            if (rem === 0) {
                                this.toastInfo(
                                    'Daily Free Actions Used (5/5)',
                                    'You have used your 5 free platform actions for today. Upgrade to the Pro Plan for unlimited two-way sync, or wait until 00:00 UTC.',
                                    5
                                );
                            }
                        }
                    }
                    return response;
                },
                async error => {
                    // Do not treat intentional client-side request cancellations or aborts as service unreachability
                    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.message === 'canceled' || !!error?.__CANCEL__) {
                        return Promise.reject(error);
                    }
                    const status = error.response?.status;
                    if (!status) {
                        this.handleServiceUnavailableError();
                        const netErr = new Error("Service host is unreachable!");
                        netErr.isNetworkError = true;
                        netErr.status = 0;
                        return Promise.reject(netErr);
                    }
                    let errorCodeMessage = {
                        400: "Bad Request!",
                        401: "Unauthorized!",
                        403: "Access Denied!",
                        404: "Resource Not Found!",
                        408: "Request Timeout!",
                        409: "Conflict!",
                        429: "Too Many Requests!",
                        500: "Internal Server Error!",
                        502: "Bad Gateway!"
                    }[status] || "Error!";

                    const errorData = error.response?.data;
                    const serverMsg = typeof errorData === 'string'
                        ? errorData
                        : errorData?.message;

                    const makeErr = (msg, extra = {}) => {
                        const errObj = (typeof error === 'object' && error !== null) ? error : new Error(msg || errorCodeMessage);
                        errObj.error = true;
                        errObj.status = status;
                        if (msg) errObj.message = msg;
                        Object.assign(errObj, extra);
                        return errObj;
                    };

                    switch (status) {
                        case 400: {
                            if (errorData?.isPermissionError) {
                                return Promise.reject(makeErr(serverMsg || error.message));
                            }
                            const validations = errorData?.errors;
                            if (Array.isArray(validations)) {
                                this.displayAndLogError(errorCodeMessage, validations.join('\n'), 20);
                                return Promise.reject(makeErr(validations.join('\n')));
                            }
                            if (typeof errorData === 'string') {
                                if (errorData.toLowerCase().includes('unauthorized azure operation')) {
                                    this.$store.commit('hasConnectedPlatform', false);
                                    this.$store.commit('hasConfiguredProject', false);
                                    this.toastWarning("Azure PAT Expired", "Your Azure DevOps personal access token has expired or is invalid. Please update your token.", 20);
                                    if (this.$route.path !== '/config' && this.$route.path !== '/account-config') {
                                        this.$router.push({ name: 'config' }).catch(() => {});
                                    }
                                    return Promise.reject(makeErr(errorData));
                                }
                                this.displayAndLogError(errorCodeMessage, errorData, 20);
                                return Promise.reject(makeErr(errorData));
                            }
                            this.displayAndLogError(errorCodeMessage, serverMsg || error.message);
                            return Promise.reject(makeErr(serverMsg || error.message));
                        }
                        case 401: {
                            const reqUrl = (error.config?.url || '').toLowerCase();
                            const isAuthRequest = reqUrl.includes('/account/signin') || 
                                                  reqUrl.includes('/account/verifygooglesignin') || 
                                                  reqUrl.includes('/account/completesignup') ||
                                                  reqUrl.includes('/account/externallogin') ||
                                                  reqUrl.includes('/account/betasignup') ||
                                                  reqUrl.includes('/account/claimbetafromsso') ||
                                                  reqUrl.includes('/account/pendingssoclaim') ||
                                                  reqUrl.includes('/account/me');
                            const isAuthRoute = this.$route.path === '/sign-in' || 
                                                  this.$route.path.startsWith('/sign-up') || 
                                                  this.$route.path === '/auth-callback' || 
                                                  !!this.$route.meta?.allowsAnonymouse;

                            if (!isAuthRequest && !isAuthRoute) {
                                if (!this.isHandlingLogout) {
                                    this.isHandlingLogout = true;
                                    this.$store.commit('signOut');
                                    if (this.$route.path !== '/') {
                                        this.$router.push('/').catch(() => {});
                                    }
                                    setTimeout(() => {
                                        this.isHandlingLogout = false;
                                    }, 3000);
                                }
                            }
                            return Promise.reject(makeErr(serverMsg || "Unauthorized Action!"));
                        }
                        case 402: {
                            if (errorData?.error === 'DailyMutationQuotaExceeded') {
                                this.$store.commit('setDailyMutationsRemaining', { remaining: 0, limit: errorData?.dailyLimit || 5 });
                            }
                            try {
                                const wsResp = await this.$http.get('/Workspace');
                                if (wsResp && wsResp.data) {
                                    this.$store.commit('setWorkspace', wsResp.data);
                                    const planMap = { Free: 0, Solo: 1, SmallTeam: 2, LargeTeam: 3, Team: 2, Business: 3, Enterprise: 4 };
                                    const currentTier = typeof wsResp.data.plan === 'number' ? wsResp.data.plan : (planMap[wsResp.data.plan] ?? 0);
                                    const rawReqPlan = errorData?.requiredPlan || 'Pro Plan';
                                    const reqTier = planMap[rawReqPlan] ?? 1;
                                    if (currentTier >= reqTier || (wsResp.data.isTrialing && !wsResp.data.trialExpired)) {
                                        return Promise.reject(makeErr("Plan state was refreshed. Please retry the operation.", { isPlanUpgrade: false, refreshed: true }));
                                    }
                                }
                            } catch (_) { /* ignore */ }
                            const rawReqPlan = errorData?.requiredPlan || 'Pro Plan';
                            const reqPlan = (rawReqPlan === 'Solo' || rawReqPlan === 'Solo Pro') ? 'Pro Plan' : rawReqPlan;
                            const customMsg = serverMsg || `This feature requires the ${reqPlan} plan.`;
                            this.toastUpgradeRequired(reqPlan, customMsg, 8);
                            this.$root.$emit('plan-upgrade-required', { requiredPlan: reqPlan });
                            return Promise.reject(makeErr("Payment / Upgrade Required", { isPlanUpgrade: true, requiredPlan: reqPlan }));
                        }
                        case 409: {
                            const conflictMsg = serverMsg || 'Operation conflicts with existing data.';
                            return Promise.reject(makeErr(conflictMsg));
                        }
                        default: {
                            if (status >= 500) {
                                this.handleHttp5xxError(error, status);
                                return Promise.reject(makeErr("Something went wrong on the server.", { status }));
                            }
                            const detailMsg = serverMsg || error.message;
                            const isInlineHandled = !!errorData?.isPermissionError || (status === 404 && (error.config?.url || '').toLowerCase().includes('/account/validateinvitation'));
                            if (!isInlineHandled) {
                                this.displayAndLogError(errorCodeMessage, detailMsg);
                            }
                            return Promise.reject(makeErr(detailMsg));
                        }
                    }
                }
            );
            
            if (this.isAuthenticated) {
                await this.initSessionConfigAsync();
                if (this.$route.path === '/') {
                    this.$router.push('/timelog').catch(() => {});
                }
            } else if (this.$route.path === '/') {
                try {
                    const meResp = await this.$http.get('/Account/Me');
                    if (meResp && meResp.data && meResp.data.email) {
                        this.$store.commit('setAccount', meResp.data);
                        this.$store.commit('setAuthenticated', true);
                        await this.initSessionConfigAsync();
                        if (this.$route.path === '/') {
                            this.$router.push('/timelog').catch(() => {});
                        }
                    }
                } catch (_) {
                    // Unauthenticated guest visitor, proceed on public routes
                }
            }
        },
        watch: {
            currentEnvironment(newEnv) {
                setEnvironmentFavicons(newEnv);
            },
            userTheme: {
                immediate: true,
                handler(theme) {
                    this.applyTheme(theme);
                }
            },
            isDemo: {
                immediate: true,
                handler(val) {
                    document.body.classList.toggle('has-demo-banner', Boolean(val));
                }
            },
            isServiceUnavailable: {
                immediate: true,
                handler(val) {
                    document.body.classList.toggle('has-offline-banner', Boolean(val));
                }
            },
            configuredProjects: {
                immediate: true,
                handler() {
                    this.initSelectedProject();
                }
            },
            '$store.state.configuredProjectSpaces': {
                immediate: true,
                deep: true,
                handler() {
                    this.initSelectedProject();
                }
            },
            globalProjectId: {
                immediate: true,
                handler(val) {
                    if (val && val !== this.navbarSelectedProjectId) {
                        this.navbarSelectedProjectId = val;
                    }
                }
            },
            async isAuthenticated(val) {
                if (val) {
                    this.initZoom();
                    await this.initSessionConfigAsync();
                    this.fetchInboxSummary();
                    if (this.$route.path === '/') {
                        this.$router.push('/timelog').catch(() => {});
                    }
                } else {
                    this.resetDisplayOptions();
                }
            },
            '$route.path': function() {
                this.isTeamMenuOpen = false;
                if (this.teamMenuLeaveTimeout) {
                    clearTimeout(this.teamMenuLeaveTimeout);
                    this.teamMenuLeaveTimeout = null;
                }
                this.fetchInboxSummary();
                if (!this.isAuthenticated) {
                    this.resetDisplayOptions();
                }
            }
        },
        created() {
            this.$store.commit('setServiceUnavailable', false);
            this.initZoom();
            this.initSelectedProject();
            this.onExitInteractiveDemo = () => {
                this.exitInteractiveDemo();
            };
            eventBus.$on('exit-interactive-demo', this.onExitInteractiveDemo);
            this.onGlobalProjectChanged = (newProjId) => {
                if (newProjId && newProjId !== this.navbarSelectedProjectId) {
                    this.navbarSelectedProjectId = newProjId;
                }
            };
            eventBus.$on('global-project-changed', this.onGlobalProjectChanged);
            this.onShowUpgradeModal = (payload) => {
                if (this.$route.path !== '/plans') {
                    this.$router.push({
                        path: '/plans',
                        query: payload ? { reason: payload.reason, requiredPlan: payload.requiredPlan } : {}
                    }).catch(() => {});
                }
            };
            eventBus.$on('show-upgrade-modal', this.onShowUpgradeModal);
            this.onDocumentClickForTeamMenu = (e) => {
                if (this.isTeamMenuOpen && !e.target.closest('.nav-team-dropdown')) {
                    this.isTeamMenuOpen = false;
                }
            };
            if (typeof document !== 'undefined') {
                document.addEventListener('click', this.onDocumentClickForTeamMenu);
            }
            this.handleGlobalKeydown = (e) => {
                const tag = e.target?.tagName;
                const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || e.target?.isContentEditable;
                if (isInput) return;

                // Help modal trigger: '?' or Shift + '/'
                if (e.key === '?' || (e.shiftKey && (e.key === '/' || e.code === 'Slash'))) {
                    e.preventDefault();
                    this.openFeedbackModal('help');
                    return;
                }

                // Global Navigation: Alt + [Key] (or Alt + Shift + [Key])
                // Tested across international/Cyrillic layouts via e.code
                if (e.altKey && !e.ctrlKey && !e.metaKey) {
                    const key = (e.key || '').toLowerCase();
                    const code = e.code || '';

                    if (key === 't' || code === 'KeyT') {
                        e.preventDefault();
                        if (this.$route.path !== '/timelog') {
                            this.$router.push('/timelog').catch(() => {});
                        }
                    } else if (key === 'k' || code === 'KeyK') {
                        e.preventDefault();
                        if (this.$route.path !== '/backlog') {
                            this.$router.push('/backlog').catch(() => {});
                        }
                    } else if (key === 'd' || code === 'KeyD' || key === 'i' || code === 'KeyI') {
                        e.preventDefault();
                        if (this.$route.path !== '/discussions') {
                            this.$router.push('/discussions').catch(() => {});
                        }
                    } else if (key === 'p' || code === 'KeyP') {
                        e.preventDefault();
                        if (this.$route.path !== '/pull-requests') {
                            this.$router.push('/pull-requests').catch(() => {});
                        }
                    } else if (key === 'b' || code === 'KeyB') {
                        e.preventDefault();
                        if (this.$route.path !== '/branches') {
                            this.$router.push('/branches').catch(() => {});
                        }
                    } else if (key === 'm' || code === 'KeyM') {
                        e.preventDefault();
                        if (this.$route.path !== '/team') {
                            this.$router.push('/team').catch(() => {});
                        }
                    } else if (key === 'c' || code === 'KeyC') {
                        e.preventDefault();
                        if (this.$route.path !== '/config') {
                            this.$router.push('/config').catch(() => {});
                        }
                    }
                }
            };
            window.addEventListener('keydown', this.handleGlobalKeydown);
            this.onHttp5xxError = (errorInfo) => {
                this.handleHttp5xxError(errorInfo, errorInfo?.status);
            };
            eventBus.$on('http-5xx-error', this.onHttp5xxError);
            this.onAdoPermissionError = (permissionData) => {
                this.toastPermissionRequired(permissionData);
            };
            eventBus.$on('ado-permission-error', this.onAdoPermissionError);
            this.onInboxSummaryUpdated = (summary) => {
                if (summary) {
                    this.inboxSummary = summary;
                } else {
                    this.fetchInboxSummary();
                }
            };
            this.$root.$on('inbox-summary-updated', this.onInboxSummaryUpdated);
            eventBus.$on('inbox-summary-updated', this.onInboxSummaryUpdated);
            this.onOpenFeedbackModal = (tab = 'overview', prefill = null) => {
                this.openFeedbackModal(tab, prefill);
            };
            eventBus.$on('open-feedback-modal', this.onOpenFeedbackModal);
            eventBus.$on('open-feedback-support-modal', this.onOpenFeedbackModal);
            this.onBetaWelcomeShown = () => {
                this.isBetaWelcomeModalOpen = true;
            };
            this.onBetaWelcomeHidden = () => {
                this.isBetaWelcomeModalOpen = false;
            };
            eventBus.$on('beta-welcome-shown', this.onBetaWelcomeShown);
            eventBus.$on('beta-welcome-hidden', this.onBetaWelcomeHidden);
            this.fetchInboxSummary();

            this._lastWasMobile = typeof window !== 'undefined' && window.innerWidth <= 1024;
            this.handleWindowResize = () => {
                const isMobile = window.innerWidth <= 1024;
                if (isMobile !== this._lastWasMobile) {
                    this._lastWasMobile = isMobile;
                    this.applyZoom();
                }
            };
            window.addEventListener('resize', this.handleWindowResize);
        },
        beforeUnmount() {
            if (this.onInboxSummaryUpdated) {
                this.$root.$off('inbox-summary-updated', this.onInboxSummaryUpdated);
                eventBus.$off('inbox-summary-updated', this.onInboxSummaryUpdated);
            }
            if (this.onAdoPermissionError) {
                eventBus.$off('ado-permission-error', this.onAdoPermissionError);
            }
            if (this.onShowUpgradeModal) {
                eventBus.$off('show-upgrade-modal', this.onShowUpgradeModal);
            }
            if (this.onExitInteractiveDemo) {
                eventBus.$off('exit-interactive-demo', this.onExitInteractiveDemo);
            }
            if (this.onGlobalProjectChanged) {
                eventBus.$off('global-project-changed', this.onGlobalProjectChanged);
            }
            if (this.onHttp5xxError) {
                eventBus.$off('http-5xx-error', this.onHttp5xxError);
            }
            if (this.onOpenFeedbackModal) {
                eventBus.$off('open-feedback-modal', this.onOpenFeedbackModal);
                eventBus.$off('open-feedback-support-modal', this.onOpenFeedbackModal);
            }
            if (this.onBetaWelcomeShown) {
                eventBus.$off('beta-welcome-shown', this.onBetaWelcomeShown);
            }
            if (this.onBetaWelcomeHidden) {
                eventBus.$off('beta-welcome-hidden', this.onBetaWelcomeHidden);
            }
            if (this.handleGlobalKeydown) {
                window.removeEventListener('keydown', this.handleGlobalKeydown);
            }
            if (this.handleWindowResize) {
                window.removeEventListener('resize', this.handleWindowResize);
            }
            if (this.reconnectTimer) {
                clearInterval(this.reconnectTimer);
                this.reconnectTimer = null;
            }
            if (this.onDocumentClickForTeamMenu && typeof document !== 'undefined') {
                document.removeEventListener('click', this.onDocumentClickForTeamMenu);
            }
            if (this.teamMenuLeaveTimeout) {
                clearTimeout(this.teamMenuLeaveTimeout);
                this.teamMenuLeaveTimeout = null;
            }
            document.body.classList.remove('has-demo-banner', 'has-offline-banner');
            if (typeof document !== 'undefined') {
                document.documentElement.style.zoom = '';
            }
        },
        methods: {
            onTeamMenuEnter() {
                if (this.teamMenuLeaveTimeout) {
                    clearTimeout(this.teamMenuLeaveTimeout);
                    this.teamMenuLeaveTimeout = null;
                }
                this.isTeamMenuOpen = true;
            },
            onTeamMenuLeave() {
                this.teamMenuLeaveTimeout = setTimeout(() => {
                    this.isTeamMenuOpen = false;
                    this.teamMenuLeaveTimeout = null;
                }, 120);
            },
            toggleTeamMenu() {
                this.isTeamMenuOpen = !this.isTeamMenuOpen;
            },
            navigateTeam(path) {
                this.isTeamMenuOpen = false;
                if (this.$route.path !== path) {
                    this.$router.push(path);
                }
            },
            async acceptGlobalTeamInviteAsync(inv) {
                if (!inv || !inv.token) return;
                try {
                    const resp = await this.$http.post(`/Workspace/Invitations/${inv.token}/Accept`);
                    const wsName = resp.data?.name || inv.workspaceName || 'the team';
                    this.toastSuccess('Joined Team!', `You have joined ${wsName}. Shared Project Spaces have been added to your workspace.`);
                    await this.initSessionConfigAsync();
                } catch (err) {
                    this.toastError('Invitation Error', err.response?.data?.message || 'Could not accept team invitation.');
                }
            },
            async declineGlobalTeamInviteAsync(inv) {
                if (!inv || !inv.token) return;
                try {
                    await this.$http.post(`/Workspace/Invitations/${inv.token}/Decline`);
                    this.toastInfo('Invitation Declined', `Declined invitation to ${inv.workspaceName}.`);
                    await this.loadTeamSessionDataAsync();
                } catch (err) {
                    this.toastError('Error', 'Could not decline invitation.');
                }
            },
            async dismissGlobalNudgeAsync(nudge) {
                if (!nudge || !nudge.id) return;
                try {
                    await this.$http.post(`/TeamReport/Nudges/${nudge.id}/Dismiss`);
                    const remaining = (this.activeTeamNudges || []).filter(n => n.id !== nudge.id);
                    this.$store.commit('setActiveTeamNudges', remaining);
                } catch (_) {
                    const remaining = (this.activeTeamNudges || []).filter(n => n.id !== nudge.id);
                    this.$store.commit('setActiveTeamNudges', remaining);
                }
            },
            async fetchInboxSummary() {
                if (!this.$store.state.isAuthenticated) return;
                try {
                    const resp = await this.$http.get('/Inbox/GetSummary');
                    if (resp && resp.data) {
                        this.inboxSummary = resp.data;
                    }
                } catch (_) {
                    // Ignore transient inbox summary fetch errors
                }
            },
            openBetaInfo() {
                if (this.isBetaWelcomeModalOpen || !this.hasAcknowledgedBetaWelcome) return;
                eventBus.$emit('open-beta-info');
            },
            openLegal(tab = 'terms') {
                eventBus.$emit('open-legal', tab);
            },
            handleServiceUnavailableError() {
                if (this.isServiceUnavailable) return;
                this.isServiceUnavailable = true;
                this.$store.commit('setServiceUnavailable', true);
                this.dismissRuntimeErrorOverlay();
                this.startReconnectCountdown();
            },
            dismissRuntimeErrorOverlay() {
                try {
                    const overlay = document.querySelector('iframe[style*="z-index: 9999999999"]') ||
                                    document.querySelector('iframe[style*="z-index: 2147483647"]');
                    if (overlay && overlay.parentNode) {
                        overlay.parentNode.removeChild(overlay);
                    }
                } catch (e) {
                    // ignore
                }
            },
            startReconnectCountdown() {
                this.dismissRuntimeErrorOverlay();
                if (this.reconnectTimer) return;
                this.reconnectCountdown = this.reconnectBackoffSeconds || 5;
                this.reconnectTimer = setInterval(async () => {
                    if (this.reconnectCountdown > 1) {
                        this.reconnectCountdown--;
                    } else {
                        clearInterval(this.reconnectTimer);
                        this.reconnectTimer = null;
                        await this.probeBackendHealthAsync();
                    }
                }, 1000);
            },
            async probeBackendHealthAsync() {
                if (this.isReconnecting) return;
                this.isReconnecting = true;
                try {
                    const resp = this.$http
                        ? await this.$http.get('/health', { timeout: 3500, withCredentials: false })
                        : await axios.get(`https://${process.env.VUE_APP_API_URI || 'beta.velocytrack.dev:44888'}/health`, {
                            timeout: 3500,
                            withCredentials: false
                        });
                    if (resp.status >= 200 && resp.status < 300) {
                        this.onServiceRecovered();
                        return;
                    }
                } catch (err) {
                    this.reconnectBackoffSeconds = Math.min((this.reconnectBackoffSeconds || 5) + 3, 15);
                } finally {
                    this.isReconnecting = false;
                    if (this.isServiceUnavailable) {
                        this.startReconnectCountdown();
                    }
                }
            },
            onServiceRecovered() {
                const wasUnavailable = this.isServiceUnavailable;
                this.isServiceUnavailable = false;
                this.$store.commit('setServiceUnavailable', false);
                this.reconnectBackoffSeconds = 5;
                if (this.reconnectTimer) {
                    clearInterval(this.reconnectTimer);
                    this.reconnectTimer = null;
                }
                if (wasUnavailable) {
                    eventBus.$emit('service-reconnected');
                }
            },
            initSelectedProject() {
                if (!this.configuredProjects || !this.configuredProjects.length) {
                    this.navbarSelectedProjectId = null;
                    return;
                }
                const savedId = this.$store.state.selectedProjectId || localStorage.getItem('velocytrack_last_project_id');
                const normSavedId = savedId ? String(savedId).toLowerCase() : null;
                const matched = normSavedId
                    ? this.configuredProjects.find(p => String(p.id).toLowerCase() === normSavedId || p.id === parseInt(savedId, 10))
                    : null;
                const active = matched || this.configuredProjects.find(p => p.isPrimary) || this.configuredProjects[0];
                if (active) {
                    this.navbarSelectedProjectId = active.id;
                    if (this.$store.state.selectedProjectId !== active.id) {
                        this.$store.commit('setSelectedProjectId', active.id);
                    }
                    if (this.$store.state.selectedProjectSpaceId !== active.id) {
                        this.$store.commit('setSelectedProjectSpaceId', active.id);
                    }
                    try {
                        localStorage.setItem('velocytrack_last_project_id', active.id);
                    } catch (e) {
                        // ignore
                    }
                }
            },
            onNavbarProjectChanged(newProjId) {
                if (newProjId === this.navbarSelectedProjectId) return;
                this.navbarSelectedProjectId = newProjId;
                if (newProjId) {
                    this.$store.commit('setSelectedProjectId', newProjId);
                    try {
                        localStorage.setItem('velocytrack_last_project_id', newProjId);
                    } catch (e) {
                        // ignore
                    }
                    eventBus.$emit('global-project-changed', newProjId);
                }
            },
            openFeedbackModal(tab = 'overview', prefill = null) {
                this.$refs.feedbackSupportModal?.openModal(tab, prefill);
            },
            handleHttp5xxError(error, status) {
                const now = Date.now();
                if (this.last5xxToastTime && now - this.last5xxToastTime < 3500) {
                    return;
                }
                this.last5xxToastTime = now;

                const endpoint = error?.config?.url || error?.endpoint || 'API endpoint';
                const method = error?.config?.method ? error.config.method.toUpperCase() : (error?.method || 'GET');
                const serverMsg = error?.response?.data?.message || (typeof error?.response?.data === 'string' ? error.response.data : error?.serverMessage) || error?.message || 'Internal Server Error';
                const timestamp = error?.timestamp || new Date().toISOString();

                const errorInfo = {
                    status: status || error?.status || 500,
                    endpoint,
                    method,
                    serverMessage: serverMsg,
                    timestamp
                };

                const h = this.$createElement;
                const vNodesMsg = h('div', { class: ['error-5xx-toast'] }, [
                    h('div', { class: ['font-weight-bold', 'mb-1', 'd-flex', 'align-items-center'] }, [
                        h('i', { class: ['bi', 'bi-exclamation-triangle-fill', 'mr-2'] }),
                        'Something went wrong!'
                    ]),
                    h('div', { class: ['small', 'mb-2', 'opacity-90'] },
                        'An unexpected error occurred while processing your request.'
                    ),
                    h('div', { class: ['d-flex', 'align-items-center', 'justify-content-between', 'mt-2', 'pt-2', 'border-top'] }, [
                        h('b-button', {
                            props: { size: 'sm', variant: 'light' },
                            class: ['font-weight-bold', 'text-danger', 'shadow-sm', 'd-inline-flex', 'align-items-center'],
                            on: {
                                click: () => this.submit5xxErrorReport(errorInfo)
                            }
                        }, [
                            h('i', { class: ['bi', 'bi-send-fill', 'mr-1'] }),
                            'Report this problem'
                        ]),
                        h('b-button', {
                            props: { size: 'sm', variant: 'link' },
                            class: ['text-white', 'small', 'p-0', 'ml-2'],
                            style: { textDecoration: 'underline' },
                            on: {
                                click: () => {
                                    this.openFeedbackModal('problem', {
                                        title: `[HTTP ${errorInfo.status}] Server error on ${errorInfo.method} ${errorInfo.endpoint}`,
                                        description: `Something went wrong while communicating with the server.\n\nEndpoint: ${errorInfo.endpoint}\nStatus: ${errorInfo.status}\nServer message: ${errorInfo.serverMessage}\nTime: ${errorInfo.timestamp}`
                                    });
                                }
                            }
                        }, 'Add details / screenshot')
                    ])
                ]);

                this.$bvToast.toast([vNodesMsg], {
                    title: 'Server Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 12000,
                    appendToast: true
                });
            },
            async submit5xxErrorReport(errorInfo) {
                try {
                    const payload = {
                        type: 'Problem',
                        category: 'Bug / Error',
                        title: `[HTTP ${errorInfo.status}] Server error on ${errorInfo.method} ${errorInfo.endpoint}`,
                        description: `Automatic 50x error report submitted by user.\n\nEndpoint: ${errorInfo.endpoint}\nMethod: ${errorInfo.method}\nHTTP Status: ${errorInfo.status}\nServer Message: ${errorInfo.serverMessage}\nTimestamp: ${errorInfo.timestamp}`,
                        diagnosticContextJson: JSON.stringify({
                            route: this.$route ? this.$route.fullPath : window.location.pathname,
                            browser: navigator.userAgent,
                            resolution: `${window.screen.width}x${window.screen.height}`,
                            zoom: `${this.currentZoom || 100}%`,
                            endpoint: errorInfo.endpoint,
                            status: errorInfo.status,
                            method: errorInfo.method,
                            timestamp: errorInfo.timestamp
                        })
                    };

                    await this.$http.post('/Feedback', payload);
                    this.toastSuccess('Problem Reported', 'Thank you! Error details and diagnostics have been sent to our team.');
                } catch (err) {
                    if (err?.status === 429 || err?.response?.status === 429) {
                        this.toastWarning('Notice', 'Report limit reached for today. Our team is already looking into this.');
                        return;
                    }
                    this.toastWarning('Notice', 'Could not send report automatically. You can report via the Help menu.');
                }
            },
            setLayoutWidth(width) {
                this.layoutWidth = width;
                if (this.isAuthenticated) {
                    try {
                        localStorage.setItem('vt_ui_layout_width', width);
                    } catch (e) {
                        // ignore
                    }
                }
            },
            setZoom(level) {
                this.currentZoom = Math.max(70, Math.min(130, level));
                if (this.isAuthenticated) {
                    try {
                        localStorage.setItem('vt_ui_zoom', this.currentZoom.toString());
                    } catch (e) {
                        // ignore
                    }
                }
                this.applyZoom();
            },
            stepZoom(delta) {
                this.setZoom(this.currentZoom + delta);
            },
            applyZoom() {
                if (typeof document !== 'undefined') {
                    const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 1024;
                    if (this.isAuthenticated && this.currentZoom && this.currentZoom !== 100 && !isMobileViewport) {
                        document.documentElement.style.zoom = (this.currentZoom / 100).toString();
                    } else {
                        document.documentElement.style.zoom = '';
                    }
                    window.dispatchEvent(new Event('resize'));
                }
            },
            getRecommendedDefaultZoom() {
                if (typeof window === 'undefined') {
                    return 100;
                }
                const viewportWidth = window.innerWidth || 0;
                const viewportHeight = window.innerHeight || 0;
                const screenWidth = (window.screen && (window.screen.availWidth || window.screen.width)) || viewportWidth;
                const screenHeight = (window.screen && (window.screen.availHeight || window.screen.height)) || viewportHeight;
                const effectiveWidth = viewportWidth > 0 ? Math.min(viewportWidth, screenWidth) : screenWidth;
                const effectiveHeight = viewportHeight > 0 ? Math.min(viewportHeight, screenHeight) : screenHeight;

                // Preserve 1:1 responsive scaling on mobile and tablet viewports (<= 1024px)
                if (effectiveWidth > 0 && effectiveWidth <= 1024) {
                    return 100;
                }

                // Laptop screens (e.g. 1366x768, 1440x900, 1536x864 @ 125% DPI, 1600x900, 1728x1117 Retina)
                if ((effectiveWidth > 0 && effectiveWidth <= 1728) || (effectiveHeight > 0 && effectiveHeight <= 960)) {
                    return 80;
                }

                // Mid-sized displays or 1080p screens with OS scaling / constrained vertical viewport
                if ((effectiveWidth <= 1920 && (window.devicePixelRatio || 1) > 1.1) || (effectiveHeight > 0 && effectiveHeight <= 1050)) {
                    return 90;
                }

                return 100;
            },
            resetDisplayOptions() {
                this.currentZoom = this.getRecommendedDefaultZoom();
                this.layoutWidth = 'standard';
                if (typeof document !== 'undefined') {
                    document.documentElement.style.zoom = '';
                    window.dispatchEvent(new Event('resize'));
                }
            },
            initZoom() {
                if (!this.isAuthenticated) {
                    this.resetDisplayOptions();
                    return;
                }
                const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 1024;
                let resolvedZoom = this.getRecommendedDefaultZoom();
                if (!isMobileViewport) {
                    try {
                        const saved = localStorage.getItem('vt_ui_zoom');
                        if (saved) {
                            const parsed = parseInt(saved, 10);
                            if (!isNaN(parsed) && parsed >= 70 && parsed <= 130) {
                                resolvedZoom = parsed;
                            }
                        }
                    } catch (e) {
                        // ignore
                    }
                }
                this.currentZoom = resolvedZoom;
                
                try {
                    const savedWidth = localStorage.getItem('vt_ui_layout_width');
                    if (savedWidth) {
                        this.layoutWidth = savedWidth;
                    }
                } catch (e) {
                    // ignore
                }
                
                this.applyZoom();
            },
            applyTheme(theme) {
                const activeTheme = theme || 'dark';
                document.documentElement.setAttribute('data-theme', activeTheme);
            },
            toggleTheme() {
                const nextTheme = this.userTheme === 'dark' ? 'light' : 'dark';
                this.$store.commit('setTheme', nextTheme);
                this.applyTheme(nextTheme);
                this.toastSuccess('Theme updated', `Switched to ${nextTheme} theme.`);
            },
            async handleSignOut() {
                try {
                    await this.$http.post('/Demo/ExitSession').catch(() => {});
                    await this.$http.get('/Account/Logout').catch(() => {});
                } catch (e) {
                    console.error('Logout error:', e);
                } finally {
                    try {
                        localStorage.removeItem('vt_mock_mode');
                        localStorage.removeItem('velocytrack_dev_mock_mode');
                        localStorage.removeItem('vt_last_data_mode');
                        localStorage.removeItem('vt_demo_state');
                        localStorage.removeItem('vt_demo_user_name');
                        localStorage.removeItem('vt_demo_user_email');
                    } catch (e) {
                        // ignore
                    }
                    this.resetDisplayOptions();
                    this.navbarSelectedProjectId = null;
                    this.$store.commit('resetTransientModeState');
                    this.$store.commit('signOut');
                    if (this.$route.path !== '/') {
                        this.$router.push('/').catch(() => {});
                    }
                }
            },
            displayAndLogError(title, message, time) {
                setTimeout(() => {
                    if (wasToastRecentlyShown(message)) return;
                    this.toastError(title, message, time);
                }, 15);
                console.error(`${title} ${message}`);
            },
            openPowerPanel() {
                if (this.isBetaWelcomeModalOpen || !this.hasAcknowledgedBetaWelcome) return;
                eventBus.$emit('open-power-panel');
            },
            openDevPanel() {
                if (this.isBetaWelcomeModalOpen || !this.hasAcknowledgedBetaWelcome) return;
                eventBus.$emit('open-dev-panel');
            },
            async exitInteractiveDemo() {
                if (this.isExitingDemo) return;
                this.$store.commit('setIsExitingDemo', true);
                try {
                    try {
                        localStorage.removeItem('vt_mock_mode');
                        localStorage.removeItem('velocytrack_dev_mock_mode');
                        localStorage.removeItem('vt_last_data_mode');
                        localStorage.removeItem('vt_demo_state');
                        localStorage.removeItem('vt_demo_user_name');
                        localStorage.removeItem('vt_demo_user_email');
                    } catch (e) {
                        // ignore
                    }

                    this.resetDisplayOptions();
                    this.navbarSelectedProjectId = null;
                    this.$store.commit('resetTransientModeState');
                    this.$store.commit('signOut');

                    try {
                        await this.$http.post('/Demo/ExitSession');
                    } catch (e) {
                        // ignore
                    }

                    if (this.$route.path !== '/') {
                        await this.$router.push('/').catch(() => {});
                    }
                } finally {
                    this.$store.commit('setIsExitingDemo', false);
                }
            },

            disableMockMode() {
                this.exitInteractiveDemo();
            }
        },
        computed: {
            mobileActiveNav() {
                const p = this.$route ? this.$route.path : '';
                if (p.startsWith('/backlog')) return { label: 'Backlog', icon: 'bi bi-list-check' };
                if (p.startsWith('/pull-requests')) return { label: 'Pull Requests', icon: 'icon-azure-pr' };
                if (p.startsWith('/branches')) return { label: 'Branches', icon: 'icon-azure-branch' };
                if (p.startsWith('/discussions') || p.startsWith('/inbox')) return { label: 'Discussions', icon: 'bi bi-chat-left-text' };
                if (p.startsWith('/team-dashboard')) return { label: 'Team Dashboard', icon: 'bi bi-speedometer2' };
                if (p.startsWith('/team') || p.startsWith('/subordinates')) return { label: 'Team Insights', icon: 'bi bi-people-fill' };
                if (p.startsWith('/config') || p.startsWith('/account-config')) return { label: 'Configuration', icon: 'bi bi-gear-fill' };
                if (p.startsWith('/profile')) return { label: 'Profile', icon: 'bi bi-person-gear' };
                if (p.startsWith('/plans')) return { label: 'Plans', icon: 'bi bi-credit-card' };
                return { label: 'Timelog', icon: 'bi bi-clock-history' };
            },
            isTeamRouteActive() {
                const p = this.$route ? this.$route.path : '';
                return p.startsWith('/team') || p.startsWith('/subordinates');
            },
            isLandingRoute() {
                return this.$route.path === '/' && !this.isAuthenticated;
            },
            layoutContainerClass() {
                if (!this.isAuthenticated) {
                    return 'container';
                }
                return this.layoutWidth === 'fluid' ? 'container-fluid px-3 px-xl-5' : 'container';
            },
            currentWorkspace() {
                return this.$store.state.workspace;
            },
            brandNavigationTarget() {
                return '/timelog';
            },
            brandNavTitle() {
                return 'VelocyTrack — Go to Timelog';
            },
            logoUrl() {
                return getLogoUrl(this.envNameNormalized);
            },
            canShowPages() {
                return this.hasConnectedPlatform && this.hasConfiguredProject;
            },
            accountDisplayName() {
                return this.account?.fullName || '';
            },
            accountAvatar() {
                return this.account?.picture || (this.account?.fullName ? `https://ui-avatars.com/api/?name=${encodeURIComponent(this.account.fullName)}&background=0284c7&color=fff` : 'https://ui-avatars.com/api/?name=User&background=0284c7&color=fff');
            },
            isDemo() {
                if (this.account?.isDemoAccount === true) return true;
                const email = (this.account?.email || '').toLowerCase();
                return email.includes('@demo');
            },
            appVersion() {
                return process.env.VUE_APP_VERSION || '0.1.0';
            },
            gitCommit() {
                return process.env.VUE_APP_GIT_COMMIT || 'local';
            },
            releaseChannel() {
                return process.env.VUE_APP_RELEASE_CHANNEL || 'Beta';
            },
            envNameNormalized() {
                return this.currentEnvironment || getAppEnvironment();
            },
            envNameDisplay() {
                const env = this.envNameNormalized;
                if (env === 'production') return 'Production';
                if (env === 'beta') return 'Beta';
                return 'Development';
            },
            envBadgeLabel() {
                const raw = this.envNameNormalized;
                if (raw === 'production' || raw === 'prod') return 'PROD';
                if (raw === 'beta' || raw === 'staging') return 'BETA';
                if (raw === 'qa') return 'QA';
                return 'DEV';
            },
            formattedBuildTime() {
                try {
                    const bt = process.env.BUILD_TIME;
                    if (!bt) return 'N/A';
                    const d = new Date(bt);
                    return d.toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
                } catch (e) {
                    return 'N/A';
                }
            }
        }
    };
</script>

<style>
    .icon-azure-branch,
    .icon-azure-pr {
        display: inline-block;
        width: 1em;
        height: 1em;
        vertical-align: -0.125em;
        background-color: currentColor;
        mask-repeat: no-repeat;
        mask-position: center;
        mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
        -webkit-mask-size: contain;
        flex-shrink: 0;
    }

    .icon-azure-branch {
        mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='black' stroke-width='1.65' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='4.5' cy='3.5' r='2'/%3E%3Ccircle cx='4.5' cy='12.5' r='2'/%3E%3Ccircle cx='11.5' cy='5.5' r='2'/%3E%3Cpath d='M4.5 5.5v5'/%3E%3Cpath d='M11.5 7.5c0 2.2-1.8 3-7 3'/%3E%3C/svg%3E");
        -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='black' stroke-width='1.65' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='4.5' cy='3.5' r='2'/%3E%3Ccircle cx='4.5' cy='12.5' r='2'/%3E%3Ccircle cx='11.5' cy='5.5' r='2'/%3E%3Cpath d='M4.5 5.5v5'/%3E%3Cpath d='M11.5 7.5c0 2.2-1.8 3-7 3'/%3E%3C/svg%3E");
    }

    .icon-azure-pr {
        mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='black' stroke-width='1.65' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='4' cy='3.5' r='2'/%3E%3Ccircle cx='4' cy='12.5' r='2'/%3E%3Ccircle cx='12' cy='12.5' r='2'/%3E%3Cpath d='M4 5.5v5'/%3E%3Cpath d='M12 10.5V6.2c0-1.5-1.2-2.7-2.7-2.7H7.2'/%3E%3Cpath d='M9 1.5L7 3.5l2 2'/%3E%3C/svg%3E");
        -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='black' stroke-width='1.65' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='4' cy='3.5' r='2'/%3E%3Ccircle cx='4' cy='12.5' r='2'/%3E%3Ccircle cx='12' cy='12.5' r='2'/%3E%3Cpath d='M4 5.5v5'/%3E%3Cpath d='M12 10.5V6.2c0-1.5-1.2-2.7-2.7-2.7H7.2'/%3E%3Cpath d='M9 1.5L7 3.5l2 2'/%3E%3C/svg%3E");
    }
    .disabled {
        cursor: not-allowed;
        pointer-events: none;
    }

    .env-raindrop-badge {
        position: absolute;
        top: 100%;
        left: 0;
        z-index: 1050;
        background-color: #17a2b8;
        color: #fff;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 0.7rem;
        text-transform: uppercase;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
        white-space: nowrap;
        pointer-events: none;
    }

    .navbar {
        padding: 0.6rem 1.25rem;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
    }

    .navbar .navbar-brand {
        font-weight: 600;
        letter-spacing: 0.02em;
        cursor: pointer;
        transition: opacity 0.15s ease, transform 0.15s ease;
    }

    .navbar .navbar-brand:hover {
        opacity: 0.92;
        color: #0f172a !important;
        transform: translateY(-1px);
    }
    [data-theme="dark"] .navbar .navbar-brand:hover {
        color: #fff !important;
    }

    .navbar-nav .nav-item .nav-link {
        padding: 0.5rem 0.9rem;
        margin: 0 0.15rem;
        border-radius: 6px;
        font-weight: 500;
        transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;
        color: #334155;
    }

    .navbar-nav .nav-item .nav-link:hover {
        color: #0f172a !important;
        background-color: rgba(0, 0, 0, 0.05);
    }

    [data-theme="dark"] .navbar-nav .nav-item .nav-link {
        color: #cbd5e1;
    }

    [data-theme="dark"] .navbar-nav .nav-item .nav-link:hover {
        color: #fff !important;
        background-color: rgba(255, 255, 255, 0.08);
    }

    .account-name {
        color: rgba(255, 255, 255, 0.85);
        font-weight: 500;
    }

    .account-avatar {
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid rgba(255, 255, 255, 0.35);
    }

    .icon-only-nav-item .nav-link {
        font-size: 1.2rem;
        padding: 0.5rem 0.7rem;
    }

    .page-content {
        min-height: 60vh;
    }

    .landing-hero {
        background: linear-gradient(135deg, #1f2933 0%, #343a40 100%);
        border-radius: 10px;
        padding: 3rem 2rem;
        text-align: center;
        color: #fff;
        margin-bottom: 2rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }

    .landing-hero-icon {
        font-size: 3.5rem;
        color: #17a2b8;
        margin-bottom: 1rem;
        display: inline-block;
    }

    .landing-hero h1 {
        font-weight: 700;
        margin-bottom: 0.75rem;
    }

    .landing-hero-subtitle {
        color: rgba(255, 255, 255, 0.75);
        max-width: 560px;
        margin: 0 auto 1.75rem;
    }

    .landing-hero-signin {
        display: flex;
        justify-content: center;
    }

    .landing-use-cases {
        margin: 0;
    }

    .use-case-card {
        border: none;
        border-radius: 10px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        transition: transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    }

    .use-case-card:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    }

    .use-case-icon {
        font-size: 1.8rem;
        color: #17a2b8;
        margin-bottom: 0.75rem;
    }

    .tooltip .tooltip-inner {
        background-color: var(--vt-tooltip-bg, #ffffff);
        color: var(--vt-tooltip-color, #0f172a);
        font-weight: 500;
        font-size: 0.8rem;
        border-radius: 6px;
        border: 1px solid var(--vt-tooltip-border, #cbd5e1);
        box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08);
        padding: 0.35rem 0.65rem;
    }

    .tooltip .arrow::before {
        border-top-color: var(--vt-tooltip-border, #cbd5e1) !important;
        border-bottom-color: var(--vt-tooltip-border, #cbd5e1) !important;
        border-left-color: var(--vt-tooltip-border, #cbd5e1) !important;
        border-right-color: var(--vt-tooltip-border, #cbd5e1) !important;
    }

    .bs-tooltip-top .arrow::before {
        border-top-color: var(--vt-tooltip-border, #cbd5e1) !important;
    }
    .bs-tooltip-top .arrow::after {
        position: absolute;
        top: 0;
        left: 0;
        content: "";
        border-color: transparent;
        border-style: solid;
        border-width: 0.4rem 0.4rem 0;
        border-top-color: var(--vt-tooltip-bg, #ffffff) !important;
        margin-top: -1px;
    }

    .bs-tooltip-bottom .arrow::before {
        border-bottom-color: var(--vt-tooltip-border, #cbd5e1) !important;
    }
    .bs-tooltip-bottom .arrow::after {
        position: absolute;
        bottom: 0;
        left: 0;
        content: "";
        border-color: transparent;
        border-style: solid;
        border-width: 0 0.4rem 0.4rem;
        border-bottom-color: var(--vt-tooltip-bg, #ffffff) !important;
        margin-bottom: -1px;
    }

    .bs-tooltip-left .arrow::before {
        border-left-color: var(--vt-tooltip-border, #cbd5e1) !important;
    }
    .bs-tooltip-left .arrow::after {
        position: absolute;
        left: 0;
        top: 0;
        content: "";
        border-color: transparent;
        border-style: solid;
        border-width: 0.4rem 0 0.4rem 0.4rem;
        border-left-color: var(--vt-tooltip-bg, #ffffff) !important;
        margin-left: -1px;
    }

    .bs-tooltip-right .arrow::before {
        border-right-color: var(--vt-tooltip-border, #cbd5e1) !important;
    }
    .bs-tooltip-right .arrow::after {
        position: absolute;
        right: 0;
        top: 0;
        content: "";
        border-color: transparent;
        border-style: solid;
        border-width: 0.4rem 0 0.4rem 0.4rem;
        border-right-color: var(--vt-tooltip-bg, #ffffff) !important;
        margin-right: -1px;
    }

    @media (min-width: 1400px) {
        .container {
            max-width: 1340px !important;
        }
    }

    @media (min-width: 1600px) {
        .container {
            max-width: 1540px !important;
        }
    }

    @media (min-width: 1800px) {
        .container {
            max-width: 1740px !important;
        }
    }

    .card-body {
        padding: 0.35rem !important;
    }

    .container .row {
        margin-left: 0;
        margin-right: 0;
    }

    .disabled-row {
        pointer-events: none;
        opacity: 0.3;
    }

    .workitem-type-image {
        width: 18px;
        padding-top: 5px;
    }

    .assigned-to-image {
        width: 36px;
        height: 36px;
    }

    .validation-pop {
        font-size: 12px !important;
    }

    button:active,
    button:focus,
    button:focus-visible {
        outline: none !important; /* Remove default outline */
        box-shadow: none !important; /* Remove box-shadow */
    }

    button {
        box-shadow: none !important; /* Ensure no box-shadow */
    }

    .selected-sprint-button {
        color: #fff !important;
        background-color: rgba(255, 255, 255, 0.2) !important;
        border-radius: 6px !important;
        border: 1px solid rgba(255, 255, 255, 0.35) !important;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25) !important;
    }

    .navbar-nav .nav-item .nav-link.router-link-exact-active,
    .navbar-nav .nav-item.router-link-exact-active .nav-link,
    .navbar-nav .nav-item .nav-link.router-link-active,
    .navbar-nav .nav-item.router-link-active .nav-link,
    .navbar-nav .nav-item .nav-link.active,
    .navbar-nav .nav-item.active .nav-link {
        color: #0284c7 !important;
        background-color: rgba(2, 132, 199, 0.08);
        border-radius: 6px;
        font-weight: 600;
    }

    [data-theme="dark"] .navbar-nav .nav-item .nav-link.router-link-exact-active,
    [data-theme="dark"] .navbar-nav .nav-item.router-link-exact-active .nav-link,
    [data-theme="dark"] .navbar-nav .nav-item .nav-link.router-link-active,
    [data-theme="dark"] .navbar-nav .nav-item.router-link-active .nav-link,
    [data-theme="dark"] .navbar-nav .nav-item .nav-link.active,
    [data-theme="dark"] .navbar-nav .nav-item.active .nav-link {
        color: #38bdf8 !important;
        background-color: rgba(56, 189, 248, 0.12);
    }

    /* Team Dropdown & Hover Menu */
    .nav-team-dropdown {
        position: relative;
    }
    .nav-team-dropdown > .nav-link {
        cursor: default;
    }
    .nav-team-dropdown .team-hover-menu {
        min-width: 250px;
        padding: 0.4rem;
        border-radius: 10px;
        margin-top: 2px;
    }
    .nav-team-dropdown .team-hover-menu .dropdown-item {
        border-radius: 6px;
        padding: 0.5rem 0.75rem;
        transition: background-color 0.15s ease;
    }
    .nav-team-dropdown .team-hover-menu .dropdown-item:hover {
        background-color: rgba(2, 132, 199, 0.08);
    }
    [data-theme="dark"] .nav-team-dropdown .team-hover-menu .dropdown-item:hover {
        background-color: rgba(56, 189, 248, 0.12) !important;
    }
    .nav-team-dropdown .team-menu-item-title {
        font-size: 0.88rem;
        line-height: 1.25;
        color: var(--vt-text, #1e293b);
    }
    [data-theme="dark"] .nav-team-dropdown .team-menu-item-title {
        color: #f1f5f9;
    }
    .nav-team-dropdown .team-menu-item-desc {
        font-size: 0.75rem;
        line-height: 1.25;
    }
    @media (hover: hover) and (pointer: fine) {
        .nav-team-dropdown:hover > .team-hover-menu {
            display: block;
        }
    }

    .b-calendar .b-calendar-grid-help {
        display: none;
    }

    .b-calendar .bg-light {
        background-color: #c9c9c9 !important;
    }

    .b-calendar .table-success,
    .b-calendar .col.table-success {
        background-color: #c3e6cb !important;
        color: #155724;
    }
    .b-calendar .table-warning,
    .b-calendar .col.table-warning {
        background-color: #ffeeba !important;
        color: #856404;
    }
    .b-calendar .table-danger,
    .b-calendar .col.table-danger {
        background-color: #f5c6cb !important;
        color: #721c24;
    }
    .b-calendar .table-info,
    .b-calendar .col.table-info {
        background-color: #bee5eb !important;
        color: #0c5460;
    }
    .b-calendar .col.table-success .btn:not(.btn-primary):not(.active),
    .b-calendar .col.table-warning .btn:not(.btn-primary):not(.active),
    .b-calendar .col.table-danger .btn:not(.btn-primary):not(.active),
    .b-calendar .col.table-info .btn:not(.btn-primary):not(.active) {
        color: inherit;
    }

    .disabled .btn.disabled, .btn:disabled, button:disabled, select:disabled, input:disabled {
        opacity: 0.4 !important;
        cursor: not-allowed;
        pointer-events: none;
    }

    .action-hover-pop,
    .modal-hover-pop,
    .nav-link-popover,
    .popover.action-hover-pop,
    .popover.modal-hover-pop,
    .tooltip {
        pointer-events: none !important;
        z-index: 1060;
    }

    body.modal-open .action-hover-pop:not(.modal-hover-pop),
    body.modal-open > .action-hover-pop:not(.modal-hover-pop) {
        display: none !important;
    }

    /* Disable all tooltips and hover popovers only on mobile screens (<768px) and touch-only devices (<992px) */
    @media (max-width: 767.98px), (max-width: 991.98px) and (hover: none) and (pointer: coarse) {
        .tooltip,
        .b-tooltip,
        .bs-tooltip-top,
        .bs-tooltip-bottom,
        .bs-tooltip-start,
        .bs-tooltip-end,
        .bs-tooltip-left,
        .bs-tooltip-right,
        .bs-tooltip-auto,
        .action-hover-pop,
        .modal-hover-pop {
            display: none !important;
            opacity: 0 !important;
            visibility: hidden !important;
            pointer-events: none !important;
        }
    }

    .nav-plan-badge {
        font-size: 0.65rem;
        background-color: rgba(255, 193, 7, 0.25);
        color: #ffc107;
        padding: 1px 5px;
        border-radius: 4px;
        margin-left: 5px;
        vertical-align: middle;
        display: inline-flex;
        align-items: center;
    }

    .nav-warning-badge {
        font-size: 0.68rem;
        color: #ffc107;
        margin-left: 5px;
        vertical-align: middle;
        display: inline-flex;
        align-items: center;
        opacity: 0.9;
        transition: transform 0.15s ease, opacity 0.15s ease;
    }

    .nav-warning-badge:hover {
        opacity: 1;
        transform: scale(1.15);
    }

    .nav-coming-next-icon {
        font-size: 0.72rem;
        color: #17a2b8;
        margin-left: 4px;
        vertical-align: middle;
        display: inline-flex;
        align-items: center;
        opacity: 0.9;
        transition: transform 0.15s ease, opacity 0.15s ease;
    }

    .nav-coming-next-icon:hover {
        opacity: 1;
        transform: scale(1.15);
    }

    /* Theme Variables & Default Dark Theme */
    :root,
    [data-theme="dark"] {
        color-scheme: dark;
        --vt-bg: #121820;
        --vt-text: #e2e8f0;
        --vt-card-bg: #1a2332;
        --vt-card-border: #2d3748;
        --vt-muted: #94a3b8;
        --vt-border: #2d3748;
        --vt-input-bg: #222d3d;
        --vt-input-color: #f1f5f9;
        --vt-input-border: #3e4c5e;
        --vt-select-icon: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%23cbd5e1' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e");
        --vt-select-icon-hover: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%23f1f5f9' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e");
        --vt-select-icon-focus: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%2338bdf8' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e");
        --vt-navbar-bg: #141b24;
        --vt-navbar-border: #232e3e;
        --vt-tooltip-bg: #1e293b;
        --vt-tooltip-color: #f8fafc;
        --vt-tooltip-border: #475569;
        --vt-scrollbar-thumb: #3b4758;
        --vt-scrollbar-thumb-hover: #4f5f76;
        --vt-scrollbar-track: rgba(18, 24, 32, 0.5);
    }

    [data-theme="light"] {
        color-scheme: light;
        --vt-bg: #f8f9fa;
        --vt-text: #212529;
        --vt-card-bg: #ffffff;
        --vt-card-border: rgba(0, 0, 0, 0.125);
        --vt-muted: #6c757d;
        --vt-border: #dee2e6;
        --vt-input-bg: #ffffff;
        --vt-input-color: #495057;
        --vt-input-border: #ced4da;
        --vt-select-icon: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%23343a40' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e");
        --vt-select-icon-hover: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%230f172a' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e");
        --vt-select-icon-focus: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%230284c7' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e");
        --vt-navbar-bg: #ffffff;
        --vt-navbar-border: #e2e8f0;
        --vt-tooltip-bg: #ffffff;
        --vt-tooltip-color: #0f172a;
        --vt-tooltip-border: #cbd5e1;
        --vt-scrollbar-thumb: #cbd5e1;
        --vt-scrollbar-thumb-hover: #94a3b8;
        --vt-scrollbar-track: rgba(0, 0, 0, 0.04);
    }

    /* Modern Custom Scrollbars matching light/dark theme */
    * {
        scrollbar-width: thin;
        scrollbar-color: var(--vt-scrollbar-thumb) var(--vt-scrollbar-track);
    }

    ::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    ::-webkit-scrollbar-track {
        background: var(--vt-scrollbar-track);
        border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb {
        background: var(--vt-scrollbar-thumb);
        border-radius: 4px;
        transition: background-color 0.15s ease;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: var(--vt-scrollbar-thumb-hover);
    }

    ::-webkit-scrollbar-corner {
        background: transparent;
    }

    html,
    body,
    #app {
        background-color: var(--vt-bg) !important;
        color: var(--vt-text) !important;
        min-height: 100vh;
        transition: background-color 0.2s ease, color 0.2s ease;
    }

    [data-theme="dark"],
    [data-theme="dark"] body,
    [data-theme="dark"] #app {
        background-color: var(--vt-bg) !important;
        color: var(--vt-text) !important;
    }

    [data-theme="dark"] .card {
        background-color: var(--vt-card-bg) !important;
        border-color: var(--vt-card-border) !important;
        color: var(--vt-text) !important;
    }

    [data-theme="dark"] .table {
        color: var(--vt-text);
    }

    [data-theme="dark"] .table-hover tbody tr:hover {
        color: var(--vt-text);
        background-color: rgba(255, 255, 255, 0.05);
    }

    [data-theme="dark"] .form-control {
        color-scheme: dark;
        background-color: var(--vt-input-bg) !important;
        color: var(--vt-input-color) !important;
        border-color: var(--vt-input-border) !important;
    }

    [data-theme="dark"] .form-control:focus {
        background-color: var(--vt-input-bg) !important;
        color: var(--vt-input-color) !important;
    }

    [data-theme="dark"] .bg-light {
        background-color: #17202c !important;
        color: var(--vt-text) !important;
    }

    [data-theme="dark"] .text-dark:not(.badge),
    [data-theme="dark"] .text-body:not(.badge) {
        color: #f1f5f9 !important;
    }

    [data-theme="dark"] .badge-warning,
    [data-theme="dark"] .badge-warning.text-dark,
    [data-theme="dark"] .badge.badge-warning {
        background-color: #ffc107 !important;
        color: #212529 !important;
    }

    [data-theme="dark"] .text-muted {
        color: var(--vt-muted) !important;
    }

    [data-theme="dark"] .modal-content {
        background-color: var(--vt-card-bg) !important;
        color: var(--vt-text) !important;
        border-color: var(--vt-card-border) !important;
    }

    [data-theme="dark"] .border,
    [data-theme="dark"] .border-top,
    [data-theme="dark"] .border-bottom,
    [data-theme="dark"] .border-left,
    [data-theme="dark"] .border-right,
    [data-theme="dark"] hr,
    [data-theme="dark"] .dropdown-divider,
    [data-theme="dark"] .modal-footer,
    [data-theme="dark"] .card-footer {
        border-color: var(--vt-border) !important;
    }

    [data-theme="dark"] .border-top {
        border-top-color: var(--vt-border) !important;
    }

    [data-theme="dark"] .border-bottom {
        border-bottom-color: var(--vt-border) !important;
    }

    [data-theme="dark"] .border {
        border-color: var(--vt-border) !important;
    }

    [data-theme="dark"] .bg-white {
        background-color: var(--vt-card-bg) !important;
        color: var(--vt-text) !important;
    }

    [data-theme="dark"] .card-header.bg-white,
    [data-theme="dark"] .card-header {
        background-color: rgba(255, 255, 255, 0.03) !important;
        border-bottom-color: var(--vt-border) !important;
        color: var(--vt-text) !important;
    }

    [data-theme="dark"] .badge-light {
        background-color: #222d3d !important;
        color: #e2e8f0 !important;
        border-color: var(--vt-border) !important;
    }

    [data-theme="dark"] input[type="date"],
    [data-theme="dark"] input[type="time"],
    [data-theme="dark"] input[type="datetime-local"],
    [data-theme="dark"] input[type="number"],
    [data-theme="dark"] input[type="month"],
    [data-theme="dark"] input[type="week"] {
        color-scheme: dark;
    }

    [data-theme="dark"] input[type="number"]::-webkit-inner-spin-button,
    [data-theme="dark"] input[type="number"]::-webkit-outer-spin-button {
        color-scheme: dark;
    }

    [data-theme="dark"] .modal-header,
    [data-theme="dark"] .modal-footer {
        border-color: var(--vt-border) !important;
    }

    [data-theme="dark"] .close {
        color: var(--vt-text) !important;
        text-shadow: none;
    }

    [data-theme="dark"] .list-group-item {
        background-color: var(--vt-card-bg) !important;
        border-color: var(--vt-border) !important;
        color: var(--vt-text) !important;
    }

    /* Spinners & Loading Overlays - Highest z-index elevation */
    .b-overlay-wrap {
        isolation: isolate;
    }

    .b-overlay {
        z-index: 2000 !important;
    }

    .b-overlay > div:first-child {
        z-index: 1;
    }

    .b-overlay > div:not(:first-child),
    .b-overlay > .position-absolute:not(:first-child),
    .b-overlay .vt-loading-indicator,
    .b-overlay .spinner-border,
    .b-overlay .spinner-grow {
        z-index: 2050 !important;
    }

    /* Standalone spinners and indicators */
    .spinner-border,
    .spinner-grow,
    .vt-loading-indicator {
        position: relative;
        z-index: 2050;
    }

    /* Overlay dark mode - deep semi-transparent backdrop */
    [data-theme="dark"] .b-overlay > div:first-child {
        background-color: rgba(15, 23, 42, 0.75) !important;
    }

    /* Full-application blocking overlay (e.g. sign-in, demo transition) */
    .app-overlay-container {
        isolation: auto !important;
    }

    .app-overlay-container > .b-overlay {
        z-index: 3000 !important;
    }

    .app-overlay-container > .b-overlay > div:not(:first-child),
    .app-overlay-container > .b-overlay .vt-loading-indicator,
    .app-overlay-container > .b-overlay .spinner-border,
    .app-overlay-container > .b-overlay .spinner-grow {
        z-index: 3050 !important;
    }

    /* Tooltip & Popover dark mode - elevated distinct surface */
    [data-theme="dark"] .popover {
        background-color: #283648 !important;
        border: 1px solid #475569 !important;
        color: #f8fafc !important;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.55), 0 4px 8px rgba(0, 0, 0, 0.4) !important;
    }
    [data-theme="dark"] .popover-header {
        background-color: #283648 !important;
        border-bottom-color: #475569 !important;
        color: #f8fafc !important;
    }
    [data-theme="dark"] .popover-body {
        color: #f1f5f9 !important;
    }
    [data-theme="dark"] .bs-popover-top > .arrow::after,
    [data-theme="dark"] .bs-popover-auto[x-placement^="top"] > .arrow::after {
        border-top-color: #283648 !important;
    }
    [data-theme="dark"] .bs-popover-top > .arrow::before,
    [data-theme="dark"] .bs-popover-auto[x-placement^="top"] > .arrow::before {
        border-top-color: #475569 !important;
    }
    [data-theme="dark"] .bs-popover-bottom > .arrow::after,
    [data-theme="dark"] .bs-popover-auto[x-placement^="bottom"] > .arrow::after {
        border-bottom-color: #283648 !important;
    }
    [data-theme="dark"] .bs-popover-bottom > .arrow::before,
    [data-theme="dark"] .bs-popover-auto[x-placement^="bottom"] > .arrow::before {
        border-bottom-color: #475569 !important;
    }
    [data-theme="dark"] .bs-popover-left > .arrow::after,
    [data-theme="dark"] .bs-popover-auto[x-placement^="left"] > .arrow::after {
        border-left-color: #283648 !important;
    }
    [data-theme="dark"] .bs-popover-left > .arrow::before,
    [data-theme="dark"] .bs-popover-auto[x-placement^="left"] > .arrow::before {
        border-left-color: #475569 !important;
    }
    [data-theme="dark"] .bs-popover-right > .arrow::after,
    [data-theme="dark"] .bs-popover-auto[x-placement^="right"] > .arrow::after {
        border-right-color: #283648 !important;
    }
    [data-theme="dark"] .bs-popover-right > .arrow::before,
    [data-theme="dark"] .bs-popover-auto[x-placement^="right"] > .arrow::before {
        border-right-color: #475569 !important;
    }
    [data-theme="dark"] .tooltip .tooltip-inner {
        background-color: var(--vt-tooltip-bg, #1e293b) !important;
        color: var(--vt-tooltip-color, #f8fafc) !important;
        border: 1px solid var(--vt-tooltip-border, #475569) !important;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5) !important;
    }
    [data-theme="dark"] .bs-tooltip-top .arrow::before {
        border-top-color: var(--vt-tooltip-border, #475569) !important;
    }
    [data-theme="dark"] .bs-tooltip-top .arrow::after {
        border-top-color: var(--vt-tooltip-bg, #1e293b) !important;
    }
    [data-theme="dark"] .bs-tooltip-bottom .arrow::before {
        border-bottom-color: var(--vt-tooltip-border, #475569) !important;
    }
    [data-theme="dark"] .bs-tooltip-bottom .arrow::after {
        border-bottom-color: var(--vt-tooltip-bg, #1e293b) !important;
    }
    [data-theme="dark"] .bs-tooltip-left .arrow::before {
        border-left-color: var(--vt-tooltip-border, #475569) !important;
    }
    [data-theme="dark"] .bs-tooltip-left .arrow::after {
        border-left-color: var(--vt-tooltip-bg, #1e293b) !important;
    }
    [data-theme="dark"] .bs-tooltip-right .arrow::before {
        border-right-color: var(--vt-tooltip-border, #475569) !important;
    }
    [data-theme="dark"] .bs-tooltip-right .arrow::after {
        border-right-color: var(--vt-tooltip-bg, #1e293b) !important;
    }

    /* DDL Options & Dropdowns — high contrast signature indicator in dark mode */
    [data-theme="dark"] .custom-select,
    [data-theme="dark"] select.form-control,
    [data-theme="dark"] select {
        color-scheme: dark;
        background-color: var(--vt-input-bg) !important;
        color: var(--vt-input-color) !important;
        border-color: var(--vt-input-border) !important;
        background-image: var(--vt-select-icon) !important;
        background-repeat: no-repeat !important;
        background-position: right 0.65rem center !important;
        background-size: 8px 10px !important;
        padding-right: 1.75rem !important;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, background-image 0.15s ease-in-out;
    }

    [data-theme="dark"] .custom-select-sm,
    [data-theme="dark"] select.form-control-sm {
        background-position: right 0.5rem center !important;
        padding-right: 1.5rem !important;
    }

    [data-theme="dark"] .custom-select:hover:not(:disabled),
    [data-theme="dark"] select.form-control:hover:not(:disabled),
    [data-theme="dark"] select:hover:not(:disabled) {
        border-color: #64748b !important;
        background-image: var(--vt-select-icon-hover) !important;
        background-repeat: no-repeat !important;
        background-position: right 0.65rem center !important;
        background-size: 8px 10px !important;
    }

    [data-theme="dark"] .custom-select:focus,
    [data-theme="dark"] select.form-control:focus,
    [data-theme="dark"] select:focus {
        border-color: #38bdf8 !important;
        background-color: var(--vt-input-bg) !important;
        color: var(--vt-input-color) !important;
        background-image: var(--vt-select-icon-focus) !important;
        background-repeat: no-repeat !important;
        background-position: right 0.65rem center !important;
        background-size: 8px 10px !important;
        box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.25) !important;
    }

    [data-theme="dark"] .custom-select:disabled,
    [data-theme="dark"] select.form-control:disabled,
    [data-theme="dark"] select:disabled {
        background-color: var(--vt-input-bg) !important;
        color: #64748b !important;
        border-color: var(--vt-input-border) !important;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='10' viewBox='0 0 4 5'%3e%3cpath fill='%2364748b' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e") !important;
        opacity: 0.65;
        cursor: not-allowed;
    }

    [data-theme="dark"] select option,
    [data-theme="dark"] .custom-select option {
        background-color: #1a2332 !important;
        color: #e2e8f0 !important;
    }
    [data-theme="dark"] .dropdown-menu {
        background-color: #1a2332 !important;
        border-color: #2d3748 !important;
        color: #e2e8f0 !important;
    }
    [data-theme="dark"] .dropdown-item {
        color: #e2e8f0 !important;
    }
    [data-theme="dark"] .dropdown-item:hover,
    [data-theme="dark"] .dropdown-item:focus {
        background-color: #2a374a !important;
        color: #ffffff !important;
    }
    [data-theme="dark"] .dropdown-divider {
        border-top-color: #2d3748 !important;
    }

    /* Switch buttons */
    [data-theme="dark"] .custom-switch .custom-control-label::before {
        background-color: #334155 !important;
        border-color: #475569 !important;
    }
    [data-theme="dark"] .custom-switch .custom-control-input:checked ~ .custom-control-label::before {
        background-color: #007bff !important;
        border-color: #007bff !important;
    }
    [data-theme="dark"] .custom-switch .custom-control-label::after {
        background-color: #cbd5e1 !important;
    }
    [data-theme="dark"] .custom-switch .custom-control-input:checked ~ .custom-control-label::after {
        background-color: #ffffff !important;
    }
    [data-theme="dark"] .custom-control-label {
        color: var(--vt-text) !important;
    }

    /* Input Addon / Hrs metric */
    [data-theme="dark"] .input-group-text {
        background-color: #222d3d !important;
        color: #94a3b8 !important;
        border-color: #3e4c5e !important;
    }

    /* Chart / Progress Cards */
    [data-theme="dark"] .time-progress-card {
        background: linear-gradient(180deg, #1a2332 0%, #151d2a 100%) !important;
        border-color: #2d3748 !important;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25) !important;
        color: var(--vt-text) !important;
    }
    [data-theme="dark"] .time-progress-title {
        color: var(--vt-text) !important;
    }

    /* Calendar in Dark Mode */
    [data-theme="dark"] .b-calendar {
        background-color: var(--vt-card-bg) !important;
        color: var(--vt-text) !important;
    }
    [data-theme="dark"] .b-calendar-inner {
        background-color: transparent !important;
    }
    [data-theme="dark"] .b-calendar-header {
        background-color: transparent !important;
        color: var(--vt-text) !important;
    }
    [data-theme="dark"] .b-calendar-nav button {
        background-color: #222d3d !important;
        border-color: #3e4c5e !important;
        color: #e2e8f0 !important;
    }
    [data-theme="dark"] .b-calendar-nav button:hover {
        background-color: #007bff !important;
        border-color: #007bff !important;
        color: #ffffff !important;
    }
    [data-theme="dark"] .b-calendar-grid {
        background-color: transparent !important;
    }
    [data-theme="dark"] .b-calendar-grid-weekdays {
        border-bottom-color: #2d3748 !important;
    }
    [data-theme="dark"] .b-calendar-grid-weekdays small {
        color: #94a3b8 !important;
    }
    [data-theme="dark"] .b-calendar-grid-body .btn.btn-light {
        background-color: transparent !important;
        color: #e2e8f0 !important;
        border-color: transparent !important;
    }
    [data-theme="dark"] .b-calendar-grid-body .btn.btn-light:hover {
        background-color: #2d3748 !important;
        color: #ffffff !important;
    }
    [data-theme="dark"] .b-calendar-grid-body .btn.btn-outline-primary {
        border-color: #3b82f6 !important;
        color: #60a5fa !important;
    }
    [data-theme="dark"] .b-calendar-grid-body .btn.btn-primary,
    [data-theme="dark"] .b-calendar-grid-body .btn.active {
        background-color: #2563eb !important;
        border-color: #2563eb !important;
        color: #ffffff !important;
    }
    [data-theme="dark"] .b-calendar .bg-light,
    [data-theme="dark"] .b-calendar .b-calendar-grid-body .col.bg-light {
        background-color: rgba(100, 116, 139, 0.22) !important;
    }
    [data-theme="dark"] .b-calendar-grid-body .btn:disabled,
    [data-theme="dark"] .b-calendar-grid-body .btn.disabled,
    [data-theme="dark"] .b-calendar .bg-light .btn {
        background-color: transparent !important;
        color: #94a3b8 !important;
        opacity: 0.85 !important;
    }
    [data-theme="dark"] .b-calendar .table-success,
    [data-theme="dark"] .b-calendar .table-success .btn {
        background-color: rgba(22, 163, 74, 0.3) !important;
        color: #86efac !important;
    }
    [data-theme="dark"] .b-calendar .table-warning,
    [data-theme="dark"] .b-calendar .table-warning .btn {
        background-color: rgba(217, 119, 6, 0.3) !important;
        color: #fde68a !important;
    }
    [data-theme="dark"] .b-calendar .table-danger,
    [data-theme="dark"] .b-calendar .table-danger .btn {
        background-color: rgba(220, 38, 38, 0.3) !important;
        color: #fca5a5 !important;
    }
    [data-theme="dark"] .b-calendar .table-info,
    [data-theme="dark"] .b-calendar .table-info .btn {
        background-color: rgba(2, 132, 199, 0.3) !important;
        color: #7dd3fc !important;
    }
    [data-theme="dark"] .calendar-legend-weekend {
        background-color: #64748b !important;
    }
    [data-theme="dark"] .legend-item {
        color: #cbd5e1 !important;
    }

    /* Absence modal calendar in dark theme */
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-caption {
        color: var(--vt-text) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-eligible {
        background-color: rgba(2, 132, 199, 0.35) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-eligible .btn {
        color: #7dd3fc !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-booked {
        background-color: rgba(217, 119, 6, 0.3) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-booked .btn {
        color: #fde68a !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-holiday {
        background-color: rgba(13, 148, 136, 0.3) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-holiday .btn {
        color: #5eead4 !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-weekend {
        background-color: rgba(100, 116, 139, 0.22) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-weekend .btn {
        color: #94a3b8 !important;
        opacity: 0.85 !important;
        background-color: transparent !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-disabled {
        background-color: rgba(100, 116, 139, 0.14) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-disabled .btn {
        color: #64748b !important;
        opacity: 0.75 !important;
        background-color: transparent !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-available:hover {
        background-color: #334155 !important;
    }

    [data-theme="dark"] .time-grid-scroll,
    [data-theme="dark"] .time-grid-content,
    [data-theme="dark"] .task-management-page,
    [data-theme="dark"] .time-management-page,
    [data-theme="dark"] .pr-management-page,
    [data-theme="dark"] .profile-page,
    [data-theme="dark"] .plans-page,
    [data-theme="dark"] .account-config-page {
        color: var(--vt-text);
    }

    [data-theme="dark"] .multiselect__tags {
        background-color: var(--vt-input-bg) !important;
        border-color: var(--vt-input-border) !important;
        color: var(--vt-input-color) !important;
    }
    [data-theme="dark"] .multiselect__input,
    [data-theme="dark"] .multiselect__single {
        background-color: var(--vt-input-bg) !important;
        color: var(--vt-input-color) !important;
    }
    [data-theme="dark"] .multiselect__content-wrapper {
        background-color: var(--vt-card-bg) !important;
        border-color: var(--vt-card-border) !important;
    }
    [data-theme="dark"] .multiselect__option {
        background-color: var(--vt-card-bg) !important;
        color: var(--vt-text) !important;
    }
    [data-theme="dark"] .multiselect__option--highlight {
        background-color: #007bff !important;
        color: #ffffff !important;
    }

    [data-theme="dark"] .paywall-card,
    [data-theme="dark"] .plan-card,
    [data-theme="dark"] .platform-card,
    [data-theme="dark"] .modal-upgrade-body {
        background: var(--vt-card-bg) !important;
        border-color: var(--vt-card-border) !important;
        color: var(--vt-text) !important;
    }

    [data-theme="dark"] .popular-card {
        background: linear-gradient(180deg, #1e293b 0%, #172554 100%) !important;
        border-color: #3b82f6 !important;
    }

    /* Alerts in Dark Mode */
    [data-theme="dark"] .alert-secondary,
    [data-theme="dark"] .alert-light {
        background-color: #1e293b !important;
        border-color: #334155 !important;
        color: #e2e8f0 !important;
    }
    [data-theme="dark"] .alert-secondary .text-muted,
    [data-theme="dark"] .alert-light .text-muted {
        color: #94a3b8 !important;
    }
    [data-theme="dark"] .alert-warning {
        background-color: rgba(245, 159, 0, 0.15) !important;
        border-color: rgba(245, 159, 0, 0.4) !important;
        color: #fef08a !important;
    }
    [data-theme="dark"] .alert-warning .alert-link {
        color: #fde047 !important;
        text-decoration: underline;
    }
    [data-theme="dark"] .alert-warning .alert-link:hover,
    [data-theme="dark"] .alert-warning .alert-link:focus {
        color: #ffffff !important;
    }
    [data-theme="dark"] .alert-info .alert-link {
        color: #38bdf8 !important;
        text-decoration: underline;
    }
    [data-theme="dark"] .alert-info .alert-link:hover {
        color: #ffffff !important;
    }
    [data-theme="dark"] .alert-success .alert-link {
        color: #4ade80 !important;
        text-decoration: underline;
    }
    [data-theme="dark"] .alert-success .alert-link:hover {
        color: #ffffff !important;
    }
    [data-theme="dark"] .alert-danger .alert-link {
        color: #f87171 !important;
        text-decoration: underline;
    }
    [data-theme="dark"] .alert-danger .alert-link:hover {
        color: #ffffff !important;
    }
    [data-theme="dark"] .alert-primary .alert-link {
        color: #60a5fa !important;
        text-decoration: underline;
    }
    [data-theme="dark"] .alert-primary .alert-link:hover {
        color: #ffffff !important;
    }
    [data-theme="dark"] .alert-secondary .alert-link,
    [data-theme="dark"] .alert-light .alert-link {
        color: #93c5fd !important;
        text-decoration: underline;
    }
    [data-theme="dark"] .alert-secondary .alert-link:hover,
    [data-theme="dark"] .alert-light .alert-link:hover {
        color: #ffffff !important;
    }
    [data-theme="dark"] .alert-warning .text-warning {
        color: #fbbf24 !important;
    }
    [data-theme="dark"] .alert-warning .btn-outline-dark,
    [data-theme="dark"] .alert-warning .btn-outline-warning {
        color: #fef08a !important;
        border-color: #eab308 !important;
        background-color: rgba(234, 179, 8, 0.15) !important;
    }
    [data-theme="dark"] .alert-warning .btn-outline-dark:hover,
    [data-theme="dark"] .alert-warning .btn-outline-warning:hover {
        color: #ffffff !important;
        border-color: #facc15 !important;
        background-color: rgba(234, 179, 8, 0.3) !important;
    }

    /* Warning Button dark mode - darkened warm amber with high contrast white text */
    [data-theme="dark"] .btn-warning {
        background-color: #b45309 !important;
        border-color: #d97706 !important;
        color: #ffffff !important;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
    }

    [data-theme="dark"] .btn-warning:hover,
    [data-theme="dark"] .btn-warning:focus,
    [data-theme="dark"] .btn-warning:active {
        background-color: #92400e !important;
        border-color: #b45309 !important;
        color: #ffffff !important;
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.35);
    }
    [data-theme="dark"] .alert-info {
        background-color: rgba(14, 165, 233, 0.15) !important;
        border-color: rgba(14, 165, 233, 0.4) !important;
        color: #7dd3fc !important;
    }
    [data-theme="dark"] .alert-danger {
        background-color: rgba(239, 68, 68, 0.15) !important;
        border-color: rgba(239, 68, 68, 0.4) !important;
        color: #fca5a5 !important;
    }
    [data-theme="dark"] .alert-success {
        background-color: rgba(34, 197, 94, 0.15) !important;
        border-color: rgba(34, 197, 94, 0.4) !important;
        color: #86efac !important;
    }

    /* Buttons & Progress in Dark Mode */
    [data-theme="dark"] .progress {
        background-color: #222d3d !important;
    }
    [data-theme="dark"] .btn-outline-secondary {
        color: #cbd5e1;
        border-color: #475569;
    }
    [data-theme="dark"] .btn-outline-secondary:hover:not(:disabled) {
        color: #ffffff;
        background-color: #334155;
        border-color: #64748b;
    }
    [data-theme="dark"] .btn-outline-secondary:disabled {
        color: #64748b;
        border-color: #334155;
    }

    /* Toast Positioning & Hierarchy (Unobtrusive Bottom-Right Corner) */
    .b-toaster[class*="b-toaster-top-"],
    .b-toaster[class*="b-toaster-bottom-"] {
        z-index: 100050 !important;
    }

    .b-toaster.b-toaster-bottom-right {
        bottom: 48px !important;
        right: 16px !important;
    }

    .b-toaster.b-toaster-bottom-right .b-toaster-slot {
        max-width: 440px !important;
        margin-left: auto;
    }

    .b-toaster .b-toast.vt-toast-permission {
        max-width: 440px !important;
        width: 440px !important;
    }

    @media (max-width: 480px) {
        .b-toaster.b-toaster-bottom-right .b-toaster-slot,
        .b-toaster .b-toast.vt-toast-permission {
            max-width: calc(100vw - 32px) !important;
            width: calc(100vw - 32px) !important;
        }
    }

    .b-toaster .b-toast {
        z-index: 100050 !important;
        margin-bottom: 0.4rem !important;
    }

    .b-toaster .b-toast .toast {
        border-radius: 8px !important;
        font-size: 0.81rem !important;
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.22) !important;
        backdrop-filter: blur(8px);
        overflow: hidden !important;
        position: relative !important;
    }

    .b-toaster .b-toast .toast-header {
        padding: 0.35rem 0.65rem !important;
        font-size: 0.81rem !important;
        border-top-left-radius: 7px !important;
        border-top-right-radius: 7px !important;
    }

    .b-toaster .b-toast .toast-body {
        padding: 0.38rem 0.65rem 0.56rem 0.65rem !important;
        font-size: 0.79rem !important;
        line-height: 1.35 !important;
        position: relative !important;
        font-size: 0.79rem !important;
        line-height: 1.35 !important;
    }

    /* Collapse empty body on title-only save toasts while keeping DOM node for E2E selectors */
    .b-toaster .b-toast.vt-toast-title-only .toast-header {
        border-bottom: none !important;
        border-radius: 7px !important;
    }

    .b-toaster .b-toast.vt-toast-title-only .toast-body {
        padding: 0 !important;
        height: 3px !important;
        min-height: 3px !important;
        overflow: hidden !important;
        opacity: 1 !important;
        border: 0 !important;
        position: relative !important;
    }

    .b-toaster .b-toast.vt-toast-title-only .vt-toast-msg-empty {
        position: absolute !important;
        left: 0 !important;
        top: 0 !important;
        width: 100% !important;
        height: 3px !important;
        opacity: 0 !important;
        overflow: hidden !important;
        font-size: 0 !important;
        line-height: 0 !important;
        pointer-events: none !important;
    }

    /* Toast Auto-Hide Progress Line */
    .vt-toast-progress-track {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 3px;
        background: rgba(148, 163, 184, 0.18);
        overflow: hidden;
        pointer-events: none;
        z-index: 2;
    }

    .vt-toast-progress-bar {
        width: 100%;
        height: 100%;
        transform-origin: left center;
        transform: scaleX(0);
        animation-name: vt-toast-progress-linear;
        animation-timing-function: linear;
        animation-fill-mode: forwards;
        animation-play-state: running;
        background: #38bdf8;
    }

    .vt-toast-progress-success .vt-toast-progress-bar {
        background: #22c55e;
    }

    .vt-toast-progress-info .vt-toast-progress-bar {
        background: #38bdf8;
    }

    .vt-toast-progress-warning .vt-toast-progress-bar {
        background: #f59e0b;
    }

    .vt-toast-progress-danger .vt-toast-progress-bar {
        background: #ef4444;
    }

    .b-toaster .b-toast:hover .vt-toast-progress-bar,
    .b-toaster .b-toast:focus-within .vt-toast-progress-bar,
    .b-toaster .b-toast[data-progress-paused="true"] .vt-toast-progress-bar {
        animation-play-state: paused !important;
    }

    @keyframes vt-toast-progress-linear {
        0% {
            transform: scaleX(0);
        }
        100% {
            transform: scaleX(1);
        }
    }

    .vt-toast-legacy-collapsed {
        padding: 0 !important;
        height: 0 !important;
        min-height: 0 !important;
        overflow: hidden !important;
        opacity: 0 !important;
        border: 0 !important;
    }

    /* Toast Notifications in Dark Mode */
    [data-theme="dark"] .b-toast .toast {
        background-color: #1a2332 !important;
        border-color: #2d3748 !important;
        color: #f1f5f9 !important;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45) !important;
    }
    [data-theme="dark"] .b-toast .toast-header {
        background-color: #17202c !important;
        border-bottom-color: #2d3748 !important;
        color: #f1f5f9 !important;
    }
    [data-theme="dark"] .b-toast .toast-body {
        color: #e2e8f0 !important;
        background-color: #1a2332 !important;
    }
    [data-theme="dark"] .b-toast .close {
        color: #94a3b8 !important;
        text-shadow: none !important;
    }
    [data-theme="dark"] .b-toast .close:hover {
        color: #ffffff !important;
    }
    [data-theme="dark"] .b-toast-success .toast-header {
        background-color: rgba(34, 197, 94, 0.25) !important;
        color: #86efac !important;
        border-bottom-color: rgba(34, 197, 94, 0.3) !important;
    }
    [data-theme="dark"] .b-toast-danger .toast-header {
        background-color: rgba(239, 68, 68, 0.25) !important;
        color: #fca5a5 !important;
        border-bottom-color: rgba(239, 68, 68, 0.3) !important;
    }
    [data-theme="dark"] .b-toast-warning .toast-header {
        background-color: rgba(245, 159, 0, 0.25) !important;
        color: #fde047 !important;
        border-bottom-color: rgba(245, 159, 0, 0.3) !important;
    }
    [data-theme="dark"] .b-toast-info .toast-header {
        background-color: rgba(14, 165, 233, 0.25) !important;
        color: #7dd3fc !important;
        border-bottom-color: rgba(14, 165, 233, 0.3) !important;
    }

    /* Permission Toast Enhancements */
    .vt-toast-permission .toast-body {
        padding: 0.65rem 0.85rem !important;
    }

    .vt-toast-permission .badge {
        white-space: normal !important;
        word-break: break-word !important;
        text-align: left !important;
        line-height: 1.35 !important;
        max-width: 100% !important;
    }

    .vt-permission-guidance {
        background-color: rgba(255, 255, 255, 0.04) !important;
        border: 1px solid var(--vt-border) !important;
        border-radius: 6px !important;
        padding: 0.6rem 0.75rem !important;
    }

    [data-theme="light"] .vt-permission-guidance {
        background-color: #f8fafc !important;
        border-color: #e2e8f0 !important;
    }

    .vt-permission-guidance-text {
        color: #cbd5e1 !important;
        font-size: 0.78rem !important;
        line-height: 1.45 !important;
    }

    [data-theme="light"] .vt-permission-guidance-text {
        color: #475569 !important;
    }

    /* Zoom & Display Scaling */
    .zoom-badge-text {
        font-size: 0.72rem;
        background: rgba(255, 255, 255, 0.15);
        padding: 1px 4px;
        border-radius: 4px;
    }
    .zoom-dropdown-menu {
        min-width: 190px;
    }

    /* Modal Backdrop Scaling Fix for CSS Zoom (prevents 80% zoom edge cutoff) */
    .modal-backdrop {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: auto !important;
        height: auto !important;
        min-width: 150vw !important;
        min-height: 150vh !important;
    }
    .modal {
        right: 0 !important;
        bottom: 0 !important;
    }

    /* Consistent top-anchored modal positioning across all pages (prevents vertical jumping on tab switch / resize) */
    @media (min-width: 768px) {
        .modal-dialog,
        .modal-dialog.modal-dialog-centered {
            margin: 1.75rem auto !important;
            min-height: 0 !important;
            align-items: flex-start !important;
            justify-content: flex-start !important;
        }
        .modal-dialog.modal-dialog-centered::before {
            display: none !important;
            height: 0 !important;
            min-height: 0 !important;
            content: none !important;
        }
        .modal-dialog.modal-dialog-scrollable,
        .modal-dialog.modal-dialog-centered.modal-dialog-scrollable {
            height: auto !important;
            max-height: calc(100% - 3.5rem) !important;
            justify-content: flex-start !important;
            align-items: stretch !important;
        }
        .modal-dialog.modal-dialog-scrollable .modal-content,
        .modal-dialog.modal-dialog-centered.modal-dialog-scrollable .modal-content {
            max-height: calc(100vh - 3.5rem) !important;
        }
    }

    /* Brand Logo in Navbar */
    .brand-logo-img {
        height: 32px;
        width: 32px;
        object-fit: contain;
        vertical-align: middle;
        flex-shrink: 0;
        border-radius: 6px;
        box-shadow: 0 0 15px rgba(56, 189, 248, 0.3);
        transition: width 0.25s ease, height 0.25s ease, box-shadow 0.25s ease;
    }

    /* 50x Error Toast */
    .error-5xx-toast {
        color: #ffffff;
    }

    /* BETA Badges */
    .beta-version-pill {
        font-size: 0.6rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        color: #0f172a;
        background: #f59e0b;
        border-radius: 4px;
        padding: 1px 6px;
        margin-left: 6px;
        vertical-align: middle;
        line-height: 1.6;
        text-transform: uppercase;
        display: inline-block;
    }
    .beta-founding-pill {
        background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
        color: #0f172a;
        box-shadow: 0 1px 3px rgba(245, 158, 11, 0.4);
    }
    .badge-beta-amber {
        background-color: #b45309 !important;
        color: #ffffff !important;
        border: 1px solid #d97706 !important;
    }
    .beta-avatar-badge {
        font-size: 0.6rem;
        letter-spacing: 0.08em;
        vertical-align: middle;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 700;
        background-color: #b45309;
        color: #ffffff;
        border: 1px solid #d97706;
    }

    /* Global Project Picker in Navbar */
    .navbar-project-picker {
        display: inline-flex;
        align-items: center;
    }
    .navbar-project-dropdown .navbar-project-dropdown-toggle {
        background-color: #f1f5f9;
        border: 1px solid #cbd5e1;
        color: #1e293b;
        font-size: 0.82rem;
        padding: 3px 8px;
        border-radius: 4px;
        line-height: 1.4;
        transition: all 0.15s ease-in-out;
    }
    .navbar-project-dropdown .navbar-project-dropdown-toggle:hover,
    .navbar-project-dropdown .navbar-project-dropdown-toggle:focus {
        background-color: #e2e8f0;
        border-color: #94a3b8;
        color: #0f172a;
        box-shadow: none;
    }
    [data-theme="dark"] .navbar-project-dropdown .navbar-project-dropdown-toggle {
        background-color: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.22);
        color: #f8fafc;
    }
    [data-theme="dark"] .navbar-project-dropdown .navbar-project-dropdown-toggle:hover,
    [data-theme="dark"] .navbar-project-dropdown .navbar-project-dropdown-toggle:focus {
        background-color: rgba(255, 255, 255, 0.18);
        border-color: rgba(255, 255, 255, 0.4);
        color: #ffffff;
        box-shadow: none;
    }
    .navbar-project-name {
        max-width: 130px;
        font-weight: 500;
        vertical-align: middle;
    }
    .navbar-project-chevron {
        font-size: 0.65rem;
        color: #94a3b8;
    }
    .navbar-project-dropdown-menu {
        min-width: 250px;
        font-size: 0.85rem;
    }
    .navbar-project-item .dropdown-item {
        padding: 0.45rem 0.85rem;
    }
    .navbar-project-label {
        font-size: 0.82rem;
        font-weight: 500;
        color: #1e293b;
        background-color: #f1f5f9;
        border: 1px solid #cbd5e1;
        padding: 3px 8px;
        border-radius: 4px;
    }
    [data-theme="dark"] .navbar-project-label {
        color: #f8fafc;
        background-color: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.22);
    }
    .platform-logo-icon {
        font-size: 0.85rem;
        vertical-align: middle;
    }
    /* Azure DevOps platform brand icon */
    .icon-azure {
        display: inline-block;
        width: 1em;
        height: 1em;
        vertical-align: -0.125em;
        background-image: url('~@/assets/azure-devops-icon.png');
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        flex-shrink: 0;
    }
    .icon-azure::before {
        content: "" !important;
        display: none !important;
    }
    .platform-logo-icon.icon-azure {
        width: 0.95em;
        height: 0.95em;
        vertical-align: middle;
    }
    /* Brand logos on navbar */
    .navbar-project-picker .brand-azure {
        color: #0078d4;
    }
    .navbar-project-picker .brand-gitlab {
        color: #fc6d26;
    }
    .navbar-project-picker .brand-github {
        color: #24292e;
    }
    .navbar-project-picker .brand-jira {
        color: #0052cc;
    }
    [data-theme="dark"] .navbar-project-picker .brand-azure {
        color: #38bdf8;
    }
    [data-theme="dark"] .navbar-project-picker .brand-gitlab {
        color: #fc6d26;
    }
    [data-theme="dark"] .navbar-project-picker .brand-github {
        color: #f1f5f9;
    }
    [data-theme="dark"] .navbar-project-picker .brand-jira {
        color: #60a5fa;
    }
    /* Brand logos inside dropdown menu */
    .navbar-project-dropdown-menu .brand-azure {
        color: #0078d4;
    }
    .navbar-project-dropdown-menu .brand-gitlab {
        color: #fc6d26;
    }
    .navbar-project-dropdown-menu .brand-github {
        color: #24292e;
    }
    .navbar-project-dropdown-menu .brand-jira {
        color: #0052cc;
    }
    /* Dark theme support for dropdown menu */
    [data-theme="dark"] .navbar-project-dropdown-menu {
        background-color: #1e293b;
        border-color: #334155;
    }
    [data-theme="dark"] .navbar-project-dropdown-menu .dropdown-header {
        color: #94a3b8;
    }
    [data-theme="dark"] .navbar-project-dropdown-menu .dropdown-item {
        color: #e2e8f0;
    }
    [data-theme="dark"] .navbar-project-dropdown-menu .dropdown-item:hover,
    [data-theme="dark"] .navbar-project-dropdown-menu .dropdown-item:focus {
        background-color: #334155;
        color: #ffffff;
    }
    [data-theme="dark"] .navbar-project-dropdown-menu .dropdown-item.active {
        background-color: #2563eb;
        color: #ffffff;
    }
    [data-theme="dark"] .navbar-project-dropdown-menu .brand-github {
        color: #f1f5f9;
    }
    [data-theme="dark"] .navbar-project-dropdown-menu .brand-azure {
        color: #38bdf8;
    }
    [data-theme="dark"] .navbar-project-dropdown-menu .brand-jira {
        color: #60a5fa;
    }

    .service-offline-banner {
        position: sticky;
        top: 0;
        z-index: 1040;
        margin: 4px 0;
        background-color: #fef3c7;
        border: 1px solid #fde68a;
        border-radius: 4px;
        color: #92400e;
        font-size: 0.8rem;
        min-height: 28px;
        line-height: 1.2;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    }

    .service-offline-banner .banner-icon {
        color: #dc2626;
        font-size: 0.95rem;
    }

    .service-offline-banner .banner-spinner {
        width: 0.85rem;
        height: 0.85rem;
        border-width: 0.15em;
        color: #b45309;
    }

    [data-theme="dark"] .service-offline-banner {
        background-color: rgba(180, 83, 9, 0.2) !important;
        border-color: rgba(217, 119, 6, 0.35) !important;
        color: #fde68a !important;
    }

    [data-theme="dark"] .service-offline-banner .banner-icon {
        color: #f87171 !important;
    }

    [data-theme="dark"] .service-offline-banner .banner-spinner {
        color: #fbbf24 !important;
    }

    .interactive-demo-banner {
        position: sticky;
        top: 0;
        z-index: 1025 !important;
        margin: 4px 0;
        background-color: #f0f9ff !important;
        border: 1px solid #7dd3fc !important;
        border-radius: 4px;
        color: #0369a1;
        font-size: 0.8rem;
        min-height: 30px;
        line-height: 1.2;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
    }

    .interactive-demo-banner .demo-banner-left {
        min-width: 0;
    }

    .interactive-demo-banner .demo-banner-text {
        min-width: 0;
    }

    .interactive-demo-banner .demo-pulse-badge {
        font-size: 0.7rem;
        letter-spacing: 0.5px;
        background-color: #0284c7;
        color: #ffffff;
        padding: 0.2rem 0.45rem;
    }

    .interactive-demo-banner .exit-demo-btn {
        font-size: 0.75rem;
        height: 24px;
        line-height: 22px;
        border-color: #0284c7;
        color: #0284c7;
        background-color: transparent;
    }

    .interactive-demo-banner .exit-demo-btn:hover {
        background-color: #0284c7;
        color: #ffffff;
    }

    .interactive-demo-banner .close-demo-icon-btn {
        width: 24px;
        height: 24px;
        line-height: 24px;
        color: #64748b !important;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .interactive-demo-banner .close-demo-icon-btn:hover {
        color: #0f172a !important;
    }

    [data-theme="dark"] .interactive-demo-banner {
        background-color: #0c4a6e !important;
        border-color: #0284c7 !important;
        color: #e0f2fe !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4) !important;
    }

    [data-theme="dark"] .interactive-demo-banner .demo-pulse-badge {
        background-color: #0284c7 !important;
        color: #ffffff !important;
    }

    [data-theme="dark"] .interactive-demo-banner .exit-demo-btn {
        border-color: #38bdf8 !important;
        color: #38bdf8 !important;
    }

    [data-theme="dark"] .interactive-demo-banner .exit-demo-btn:hover {
        background-color: #0284c7 !important;
        color: #ffffff !important;
    }

    [data-theme="dark"] .interactive-demo-banner .close-demo-icon-btn {
        color: #94a3b8 !important;
    }

    [data-theme="dark"] .interactive-demo-banner .close-demo-icon-btn:hover {
        color: #f1f5f9 !important;
    }

    .landing-layout-wrapper {
        width: 100%;
        max-width: 100%;
        padding: 0;
        margin: 0;
    }

    /* Shell Layout & Full-Width Themed Navbar */
    .app-layout-root {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
    }

    .service-offline-banner-wrap {
        width: 100%;
        background-color: #fef3c7;
        border-bottom: 1px solid #fde68a;
    }

    [data-theme="dark"] .service-offline-banner-wrap {
        background-color: rgba(180, 83, 9, 0.2);
        border-bottom: 1px solid rgba(217, 119, 6, 0.35);
    }

    .interactive-demo-banner-wrap {
        width: 100%;
        background-color: #f0f9ff;
        border-bottom: 1px solid #7dd3fc;
    }

    [data-theme="dark"] .interactive-demo-banner-wrap {
        background-color: rgba(2, 132, 199, 0.15);
        border-bottom: 1px solid rgba(56, 189, 248, 0.3);
    }

    .app-header-shell {
        width: 100%;
        background-color: var(--vt-navbar-bg, #ffffff);
        border-bottom: 1px solid var(--vt-navbar-border, #e2e8f0);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
        position: relative;
        z-index: 1000;
        transition: background-color 0.2s ease, border-color 0.2s ease;
    }

    [data-theme="dark"] .app-header-shell {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
    }

    .brand-title {
        color: #0f172a !important;
        font-weight: 700;
        letter-spacing: -0.02em;
    }
    .brand-title .brand-name {
        background: linear-gradient(135deg, #0f172a 40%, #475569 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }
    [data-theme="dark"] .brand-title {
        color: #ffffff !important;
    }
    [data-theme="dark"] .brand-title .brand-name {
        background: linear-gradient(135deg, #ffffff 40%, #94a3b8 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .zoom-nav-btn,
    .icon-only-nav-item .nav-link {
        color: #475569 !important;
        transition: color 0.15s ease;
    }
    .zoom-nav-btn:hover,
    .icon-only-nav-item .nav-link:hover {
        color: #0f172a !important;
    }
    [data-theme="dark"] .zoom-nav-btn,
    [data-theme="dark"] .icon-only-nav-item .nav-link {
        color: #cbd5e1 !important;
    }
    [data-theme="dark"] .zoom-nav-btn:hover,
    [data-theme="dark"] .icon-only-nav-item .nav-link:hover {
        color: #ffffff !important;
    }

    .navbar-center-nav .nav-link {
        font-weight: 500;
        font-size: 0.88rem;
        padding: 0.35rem 0.7rem !important;
        border-radius: 6px;
        transition: all 0.15s ease;
        color: #334155 !important;
    }
    .navbar-center-nav .nav-link:hover {
        background-color: rgba(0, 0, 0, 0.05) !important;
        color: #0f172a !important;
    }
    .navbar-center-nav .nav-link.active,
    .navbar-center-nav .nav-link.router-link-active,
    .navbar-center-nav .nav-link.router-link-exact-active,
    .navbar-center-nav .router-link-active,
    .navbar-center-nav .router-link-exact-active {
        color: #0284c7 !important;
        background-color: rgba(2, 132, 199, 0.08) !important;
        font-weight: 600;
    }
    [data-theme="dark"] .navbar-center-nav .nav-link {
        color: #cbd5e1 !important;
    }
    [data-theme="dark"] .navbar-center-nav .nav-link:hover {
        background-color: rgba(255, 255, 255, 0.08) !important;
        color: #ffffff !important;
    }
    [data-theme="dark"] .navbar-center-nav .nav-link.active,
    [data-theme="dark"] .navbar-center-nav .nav-link.router-link-active,
    [data-theme="dark"] .navbar-center-nav .nav-link.router-link-exact-active,
    [data-theme="dark"] .navbar-center-nav .router-link-active,
    [data-theme="dark"] .navbar-center-nav .router-link-exact-active {
        color: #38bdf8 !important;
        background-color: rgba(56, 189, 248, 0.12) !important;
    }

    .vt-navbar {
        padding: 0.45rem 0 !important;
        background-color: transparent !important;
        box-shadow: none !important;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .navbar-left-section {
        flex: 1 1 0;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        min-width: 0;
    }

    .navbar-center-nav {
        flex: 0 0 auto;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
    }

    .navbar-right-controls {
        flex: 1 1 0;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
    }

    .app-content-container {
        flex: 1 0 auto;
        padding-top: 1rem;
        padding-bottom: 2rem;
    }

    .landing-layout-wrapper.app-content-container {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }

    .nav-inbox-badge {
        font-size: 0.68rem;
        padding: 0.15rem 0.38rem;
        vertical-align: middle;
    }

    @media (max-width: 991.98px) {
        .vt-navbar {
            flex-wrap: wrap;
        }
        .navbar-left-section,
        .navbar-right-controls {
            flex: 0 0 auto;
        }
        .navbar-center-nav {
            order: 3;
            width: 100%;
            overflow: visible;
            padding-top: 0.3rem;
            margin-top: 0.25rem;
            border-top: 1px solid var(--vt-navbar-border, #e2e8f0);
            justify-content: space-between;
        }
        .nav-team-dropdown .team-hover-menu {
            right: 0;
            left: auto;
            padding-top: 0.25rem;
        }
    }

    @media (max-width: 767.98px) {
        .app-content-container:not(.landing-layout-wrapper) {
            padding-top: 0.5rem;
            padding-bottom: 1.25rem;
            padding-left: 0.5rem;
            padding-right: 0.5rem;
        }
        .interactive-demo-banner-wrap > .container,
        .app-header-shell > .container {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
        }
        .interactive-demo-banner {
            margin: 3px 0;
            padding: 0.25rem 0.5rem !important;
            font-size: 0.75rem;
            min-height: 28px;
        }
        .interactive-demo-banner .demo-pulse-badge {
            font-size: 0.65rem;
            padding: 0.18rem 0.4rem;
        }
        .interactive-demo-banner .exit-demo-btn {
            font-size: 0.72rem;
            height: 22px;
            line-height: 20px;
            white-space: nowrap;
        }
        .navbar-center-nav {
            display: none !important;
            grid-template-columns: repeat(6, minmax(0, 1fr));
            gap: 2px;
        }
        .navbar-center-nav .nav-item {
            min-width: 0;
            text-align: center;
        }
        .navbar-center-nav .nav-item .nav-link {
            margin: 0 !important;
            padding: 0.28rem 0.15rem !important;
            font-size: 0.65rem !important;
            line-height: 1.15;
            display: flex !important;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            position: relative;
        }
        .navbar-center-nav .nav-item .nav-link > i:first-child {
            font-size: 0.98rem;
            margin-right: 0 !important;
            margin-bottom: 2px;
        }
        .navbar-center-nav .nav-item .nav-link.dropdown-toggle::after {
            display: none !important;
        }
        .navbar-center-nav .nav-warning-badge,
        .navbar-center-nav .nav-coming-next-icon {
            position: absolute;
            top: 2px;
            right: 4px;
            margin-left: 0 !important;
            font-size: 0.58rem;
        }
        .navbar-center-nav .nav-inbox-badge {
            position: absolute;
            top: 1px;
            right: 2px;
            margin-left: 0 !important;
            font-size: 0.56rem;
            padding: 0.1rem 0.28rem;
        }
    }

    @media (max-width: 991.98px) {
        .vt-navbar {
            flex-wrap: nowrap !important;
            padding: 0.35rem 0.25rem !important;
        }

        html,
        body {
            overflow-x: hidden;
            width: 100%;
            max-width: 100%;
        }

        .dev-trigger-tab,
        .power-trigger-tab {
            display: none !important;
        }

        .navbar-center-nav {
            display: none !important;
        }

        .navbar-left-section,
        .navbar-right-controls {
            flex: 0 0 auto !important;
        }

        .mobile-center-nav-dropdown .mobile-nav-toggle-btn {
            border-radius: 9999px !important;
            font-size: 0.85rem !important;
            padding: 0.32rem 0.85rem !important;
            border: 1px solid var(--vt-border, rgba(148, 163, 184, 0.35)) !important;
            background-color: rgba(148, 163, 184, 0.1) !important;
            color: var(--vt-text, #f8fafc) !important;
        }

        [data-theme="dark"] .mobile-center-nav-dropdown .mobile-nav-toggle-btn {
            background-color: rgba(30, 41, 59, 0.85) !important;
            border-color: rgba(56, 189, 248, 0.35) !important;
            color: #f8fafc !important;
        }

        .mobile-center-nav-dropdown .mobile-nav-dropdown-menu {
            min-width: 210px;
            border-radius: 10px;
            left: 50% !important;
            transform: translateX(-50%) !important;
            margin-top: 0.35rem !important;
        }
    }

    @media (min-width: 768px) and (max-width: 991.98px) {
        .interactive-demo-banner-wrap > .container,
        .app-header-shell > .container,
        .app-content-container.container:not(.landing-layout-wrapper) {
            max-width: 100% !important;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
        }
    }

    @media (max-width: 575.98px) {
        .demo-modal-dark .input-group-text {
            font-size: 0.78rem;
            padding: 0.375rem 0.5rem;
        }
        /* Hide floating left edge trigger tabs on mobile phone size (accessed via User Menu) */
        .dev-trigger-tab,
        .power-trigger-tab {
            display: none !important;
        }

        /* Full screen drawer panels on mobile phone size */
        .power-drawer-panel,
        .dev-drawer-panel {
            width: 100% !important;
            width: 100vw !important;
            max-width: 100% !important;
            max-width: 100vw !important;
            height: 100% !important;
            height: 100vh !important;
            height: 100dvh !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            border-radius: 0 !important;
            border: none !important;
            z-index: 1060 !important;
        }

        .power-drawer-backdrop,
        .dev-drawer-backdrop {
            z-index: 1055 !important;
        }
    }

    /* Filter Toolbar: Shake & Strong Glowing Pulse Apply Button on Unapplied Filter Changes */
    .btn-apply-pulse {
        position: relative;
        z-index: 2;
        font-weight: 600;
        animation: vt-apply-shake 2.8s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite,
                   vt-apply-pulse 1.4s ease-in-out infinite;
        border-color: #0284c7 !important;
        will-change: transform, box-shadow, filter;
    }

    .btn-apply-pulse:disabled,
    .btn-apply-pulse.disabled {
        animation: none !important;
        box-shadow: none !important;
        filter: none !important;
        transform: none !important;
    }

    /* Timelog-inspired shake vibration: shakes in bursts every 2.8s */
    @keyframes vt-apply-shake {
        0% {
            transform: translate3d(0, 0, 0);
        }
        3%, 27% {
            transform: translate3d(-3px, 0, 0);
        }
        6%, 24% {
            transform: translate3d(5px, 0, 0);
        }
        9%, 15%, 21% {
            transform: translate3d(-7px, 0, 0);
        }
        12%, 18% {
            transform: translate3d(7px, 0, 0);
        }
        30% {
            transform: translate3d(0, 0, 0);
        }
        100% {
            transform: translate3d(0, 0, 0);
        }
    }

    /* High-contrast, strong luminous pulse (Light theme) */
    @keyframes vt-apply-pulse {
        0% {
            box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.85);
            border-color: #0284c7 !important;
            filter: brightness(1);
        }
        50% {
            box-shadow: 0 0 0 5px rgba(2, 132, 199, 0.55), 0 0 20px 4px rgba(14, 165, 233, 0.85), 0 0 35px rgba(56, 189, 248, 0.55);
            border-color: #38bdf8 !important;
            filter: brightness(1.2);
        }
        100% {
            box-shadow: 0 0 0 0 rgba(2, 132, 199, 0);
            border-color: #0284c7 !important;
            filter: brightness(1);
        }
    }

    /* High-contrast, strong luminous pulse (Dark theme) */
    [data-theme="dark"] .btn-apply-pulse {
        animation: vt-apply-shake 2.8s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite,
                   vt-apply-pulse-dark 1.4s ease-in-out infinite;
        border-color: #38bdf8 !important;
    }

    [data-theme="dark"] .btn-apply-pulse:disabled,
    [data-theme="dark"] .btn-apply-pulse.disabled {
        animation: none !important;
        box-shadow: none !important;
        filter: none !important;
        transform: none !important;
    }

    @keyframes vt-apply-pulse-dark {
        0% {
            box-shadow: 0 0 0 0 rgba(56, 189, 248, 0.95);
            border-color: #38bdf8 !important;
            filter: brightness(1);
        }
        50% {
            box-shadow: 0 0 0 6px rgba(56, 189, 248, 0.65), 0 0 24px 6px rgba(56, 189, 248, 0.9), 0 0 42px rgba(14, 165, 233, 0.65);
            border-color: #bae6fd !important;
            filter: brightness(1.25);
        }
        100% {
            box-shadow: 0 0 0 0 rgba(56, 189, 248, 0);
            border-color: #38bdf8 !important;
            filter: brightness(1);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .btn-apply-pulse,
        [data-theme="dark"] .btn-apply-pulse {
            animation: none !important;
            box-shadow: 0 0 0 4px rgba(2, 132, 199, 0.6) !important;
            filter: none !important;
            transform: none !important;
        }
    }

    /* Full-Screen Mobile Modals (<768px) - Edge-to-edge with zero frames */
    @media (max-width: 767.98px) {
        .modal {
            padding: 0 !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            overflow: hidden !important;
        }

        .modal-dialog:not(.modal-sm):not(.modal-dialog-confirm),
        .modal-dialog.modal-dialog-pick-wi,
        .modal-dialog.modal-dialog-apply-absence,
        .modal-dialog.modal-dialog-elevated,
        .modal-dialog.modal-dialog-scrollable,
        .modal-dialog.modal-lg,
        .modal-dialog.modal-xl,
        .modal .modal-dialog:not(.modal-sm):not(.modal-dialog-confirm) {
            margin: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            height: 100% !important;
            min-height: 100% !important;
            min-height: 100vh !important;
            min-height: 100dvh !important;
            transform: none !important;
            display: flex !important;
            flex-direction: column !important;
        }

        .modal-dialog-centered::before {
            display: none !important;
            height: 0 !important;
            min-height: 0 !important;
            content: none !important;
        }

        .modal-dialog:not(.modal-sm):not(.modal-dialog-confirm) .modal-content,
        .modal-dialog-scrollable .modal-content {
            margin: 0 !important;
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            outline: none !important;
            width: 100% !important;
            height: 100% !important;
            min-height: 100vh !important;
            min-height: 100dvh !important;
            max-height: none !important;
            flex: 1 1 100% !important;
            display: flex !important;
            flex-direction: column !important;
        }

        .modal-header {
            padding: 0.7rem 0.9rem !important;
            padding-top: max(0.7rem, env(safe-area-inset-top)) !important;
            padding-left: max(0.9rem, env(safe-area-inset-left)) !important;
            padding-right: max(0.9rem, env(safe-area-inset-right)) !important;
            border-radius: 0 !important;
            border-bottom: 1px solid var(--vt-border, rgba(148, 163, 184, 0.25)) !important;
            flex-shrink: 0 !important;
            align-items: center !important;
        }

        .modal-header .modal-title {
            font-size: 1.05rem !important;
            font-weight: 600 !important;
            line-height: 1.3 !important;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .modal-header .close {
            padding: 0.5rem 0.75rem !important;
            margin: -0.5rem 0 -0.5rem auto !important;
            font-size: 1.5rem !important;
            opacity: 0.8 !important;
        }

        .modal-dialog:not(.modal-sm):not(.modal-dialog-confirm) .modal-body {
            padding: 0.85rem 0.9rem !important;
            padding-left: max(0.9rem, env(safe-area-inset-left)) !important;
            padding-right: max(0.9rem, env(safe-area-inset-right)) !important;
            flex: 1 1 auto !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch !important;
            max-height: none !important;
            height: auto !important;
        }

        .modal-dialog:not(.modal-sm):not(.modal-dialog-confirm) .modal-footer {
            padding: 0.65rem 0.9rem !important;
            padding-bottom: max(0.65rem, env(safe-area-inset-bottom)) !important;
            padding-left: max(0.9rem, env(safe-area-inset-left)) !important;
            padding-right: max(0.9rem, env(safe-area-inset-right)) !important;
            border-radius: 0 !important;
            border-top: 1px solid var(--vt-border, rgba(148, 163, 184, 0.25)) !important;
            flex-shrink: 0 !important;
            flex-wrap: wrap !important;
            gap: 0.35rem !important;
            margin-top: auto !important;
        }

        .modal-footer .btn {
            font-size: 0.85rem !important;
            padding: 0.4rem 0.75rem !important;
        }

        /* Small / Confirmation Modals on Mobile (<768px): Keep as clean centered dialogs */
        .modal-dialog.modal-sm,
        .modal-dialog.modal-dialog-confirm,
        .modal-dialog.b-modal-msg-box,
        .modal-dialog-confirm {
            margin: auto !important;
            max-width: min(92vw, 380px) !important;
            width: 92% !important;
            height: auto !important;
            min-height: auto !important;
            position: relative !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
            display: flex !important;
            flex-direction: column !important;
        }

        .modal-dialog.modal-sm .modal-content,
        .modal-dialog.modal-dialog-confirm .modal-content,
        .modal-dialog.b-modal-msg-box .modal-content,
        .modal-dialog-confirm .modal-content {
            margin: 0 !important;
            width: 100% !important;
            height: auto !important;
            min-height: auto !important;
            max-height: calc(85vh - 2rem) !important;
            max-height: calc(85dvh - 2rem) !important;
            border-radius: 0.75rem !important;
            border: 1px solid var(--vt-border, rgba(148, 163, 184, 0.25)) !important;
            box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45) !important;
            flex: 0 1 auto !important;
            background-color: var(--vt-card-bg, #ffffff) !important;
            overflow: hidden !important;
        }

        [data-theme="dark"] .modal-dialog.modal-sm .modal-content,
        [data-theme="dark"] .modal-dialog.modal-dialog-confirm .modal-content,
        [data-theme="dark"] .modal-dialog.b-modal-msg-box .modal-content,
        [data-theme="dark"] .modal-dialog-confirm .modal-content {
            background-color: var(--vt-card-bg, #1e293b) !important;
            border-color: var(--vt-card-border, rgba(148, 163, 184, 0.22)) !important;
        }

        .modal-dialog.modal-sm .modal-header,
        .modal-dialog.modal-dialog-confirm .modal-header,
        .modal-dialog.b-modal-msg-box .modal-header,
        .modal-dialog-confirm .modal-header {
            padding: 0.75rem 1rem 0.25rem 1rem !important;
            border-bottom: none !important;
        }

        .modal-dialog.modal-sm .modal-body,
        .modal-dialog.modal-dialog-confirm .modal-body,
        .modal-dialog.b-modal-msg-box .modal-body,
        .modal-dialog-confirm .modal-body {
            flex: 0 1 auto !important;
            height: auto !important;
            padding: 1rem 1rem 0.75rem 1rem !important;
            font-size: 0.92rem !important;
            line-height: 1.45 !important;
            overflow-y: auto !important;
        }

        .modal-dialog.modal-sm .modal-footer,
        .modal-dialog.modal-dialog-confirm .modal-footer,
        .modal-dialog.b-modal-msg-box .modal-footer,
        .modal-dialog-confirm .modal-footer {
            margin-top: 0 !important;
            flex-shrink: 0 !important;
            flex-wrap: nowrap !important;
            justify-content: flex-end !important;
            padding: 0.5rem 1rem 0.85rem 1rem !important;
            border-top: none !important;
            gap: 0.5rem !important;
        }

        .modal-dialog.modal-sm .modal-footer .btn,
        .modal-dialog.modal-dialog-confirm .modal-footer .btn,
        .modal-dialog.b-modal-msg-box .modal-footer .btn,
        .modal-dialog-confirm .modal-footer .btn {
            min-width: 5rem;
            padding: 0.4rem 0.85rem !important;
            font-size: 0.88rem !important;
            font-weight: 500 !important;
        }
    }

    </style>

